<template>
  <div v-if="preloader">
    <template>
      <bubble-loader/>
    </template>
  </div>

  <div v-else>
    <div v-if="isMobile" class="mobile-test-report-shell">
      <div class="mobile-test-report-sticky-header">
      <div class="mobile-test-report-header d-flex align-center px-4">
        <v-btn icon large class="mr-1" @click="goBack">
          <v-icon size="28">mdi-arrow-left</v-icon>
        </v-btn>
        <div class="text-h6 font-weight-bold">Test Report</div>
      </div>
      <v-tabs
        v-if="assessmentType?.toLowerCase() === 'dynamic'"
        :value="isSelect"
        class="mobile-test-report-tabs"
        show-arrows
        hide-slider
        @change="switchResult($event)"
      >
        <v-tab
          v-for="(btn, index) in mobileStageButtons"
          :key="btn.status || index"
          :disabled="btn.disable"
        >
          {{ btn.status }}
        </v-tab>
      </v-tabs>
      <v-card class="mobile-candidate-card mx-4 mt-3 rounded-xl" outlined elevation="0">
        <div
          class="d-flex align-center justify-space-between pa-4 cursor"
          @click="mobileCandidateCardExpanded = !mobileCandidateCardExpanded"
        >
          <div>
            <p class="font-weight-bold text-h6 mb-0">{{ selectedUserName | capitalizeFirst }}</p>
            <p class="subtitle-2 mb-0">{{ userEmail?.toLowerCase() }}</p>
          </div>
          <v-icon>{{ mobileCandidateCardExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </div>
        <v-expand-transition>
          <div v-if="mobileCandidateCardExpanded" class="px-4 pb-4 mobile-candidate-card-details">
            <v-divider class="mb-3"></v-divider>
            <div class="d-flex justify-space-between mobile-detail-row">
              <span>Test Taken On</span><span>{{ selectedResult.testTakenOn }}</span>
            </div>
            <div class="d-flex justify-space-between mobile-detail-row">
              <span>Exit Full Screen</span><span>{{ proctoringData["Exit Fullscreen"] }}</span>
            </div>
            <div class="d-flex justify-space-between mobile-detail-row">
              <span>Screenshot Attempted</span><span>{{ proctoringData.Screenshot }}</span>
            </div>
            <div class="d-flex justify-space-between mobile-detail-row">
              <span>Total Time Taken</span><span>{{ userTime }} mins</span>
            </div>
            <div class="d-flex justify-space-between mobile-detail-row">
              <span>Screen Change</span><span>{{ proctoringData["Switch Tab/Browser"] }}</span>
            </div>
            <div class="d-flex justify-space-between mobile-detail-row">
              <span>Assessment Type</span><span>{{ assessmentType | capitalizeFirst }} Assessment</span>
            </div>
          </div>
        </v-expand-transition>
      </v-card>
      </div>

      <div class="mobile-report-body">
      <v-card class="mobile-total-score-card mx-4 mt-3 pa-4 rounded-xl" outlined elevation="0">
        <div class="d-flex align-center justify-space-between">
          <div>
            <div class="text-subtitle-1 font-weight-bold">Total Score</div>
            <div class="text-body-2">({{ totalScore }}/{{ maxMarks }})</div>
          </div>
          <v-menu bottom offset-x offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon outlined class="mobile-score-download-btn" :loading="downloadLoader" v-bind="attrs" v-on="on">
                <v-icon color="#3F41D1">mdi-import mdi-rotate-90</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item @click="openTestReport">
                Test Report
              </v-list-item>
              <v-list-item v-if="assessmentType.toLowerCase() === 'dynamic' && isSelect === 1 && hasPsychometry" @click="generatePsychometryReport" :disabled="disablePsychometryDownload">
                Psychometry Report
              </v-list-item>
              <v-list-item v-if="['userPerformanceReport', 'psychometryReport'].includes(campaignMetaData?.reportGeneration?.reportType)" @click="openAdvancedReport" :disabled="disableAdvanceReportDownload">
                {{assessmentType.toLowerCase() === 'dynamic' ? 'Advanced Report' : 'Psychometry Report'}}
              </v-list-item>
              <v-list-item v-if="assessmentType.toLowerCase() === 'dynamic' && campaignMetaData?.reportGeneration?.reportType === 'gtoReport'" @click="openAdvancedReport" :disabled="disableAdvanceReportDownload">
                GTO Report
              </v-list-item>
            </v-list>
          </v-menu>
        </div>

        <div class="mobile-score-bar-wrapper mt-2">
          <div class="mobile-score-marker mobile-score-marker--user" :style="`left:${Math.max(progressLevel, 3)}%`">
            <img src="../../public/svgs/CurrentUserIcon.svg" alt="user score" />
            <span>{{ Math.round(progressLevel) }}</span>
          </div>
          <v-progress-linear
            v-model="progressLevel"
            rounded
            :color="(assessmentType?.toLowerCase() === 'dynamic' && selectedSkill.result !== 'PASSED') ? 'error' : '#39AD37'"
            height="10"
            style="pointer-events: none"
          ></v-progress-linear>
          <div
            v-if="assessmentType.toLowerCase() === 'dynamic' && campaignMetaData?.isSkillLevelPassFailApplicable"
            class="mobile-score-marker mobile-score-marker--passing"
            :style="`left:${((selectedSkill.threhold) / maxMarks)*100}%`"
          >
            <img src="../../public/svgs/PassingIcon.svg" alt="passing score" />
            <span>{{ Math.round((selectedSkill.threhold / maxMarks) * 100) }}</span>
          </div>
        </div>

        <div class="d-flex align-center justify-end mt-2">
          <template v-if="assessmentType.toLowerCase() === 'dynamic' && campaignMetaData?.isSkillLevelPassFailApplicable">
            <img class="mr-2" src="../../public/svgs/PassingIcon.svg" alt="pass reference icon" />
            <div class="text-body-2 mr-4">Passing Score</div>
          </template>
          <img class="mr-2" src="../../public/svgs/CurrentUserUpIcon.svg" alt="current reference icon">
          <div class="text-body-2">User's score</div>
        </div>
      </v-card>

      <div class="mobile-skill-cards-grid mx-4 mt-3">
        <v-card
          v-for="(skill, index) in selectedResult.skills"
          :key="skill.skillName || index"
          class="mobile-skill-card pa-3 rounded-xl"
          :class="{ 'mobile-skill-card--disabled': !skill.questions }"
          outlined
          elevation="0"
          @click="skill.questions && getSelectedSkillData(skill, index)"
        >
          <div class="text-subtitle-2 font-weight-bold text-capitalize mb-2">{{ skill.skillName }}</div>
          <div v-if="skill?.weightage" class="d-flex justify-space-between mobile-skill-card-row">
            <span>Weightage</span><span class="font-weight-bold">{{ skill.weightage }}%</span>
          </div>
          <div class="d-flex justify-space-between mobile-skill-card-row">
            <span>Score</span>
            <span class="font-weight-bold">{{ Number.isFinite(skill?.score) ? `${skill?.score || 0}/${skill?.maximumMarks}` : 'NA' }}</span>
          </div>
          <div class="d-flex justify-space-between mobile-skill-card-row">
            <span>Time Taken</span><span class="font-weight-bold">{{ getTotalTimeForSkill(skill.questions || []) }} mins</span>
          </div>
          <div class="d-flex justify-space-between mobile-skill-card-row">
            <span>Total Questions</span><span class="font-weight-bold">{{ skill.questions?.length ?? 'NA' }}</span>
          </div>
        </v-card>
      </div>
      </div>
    </div>
    <template v-else>
    <v-card
      height="64px"
      width="100%"
      class="background ml-4 mr-6 my-0 px-4 transparent elevation-0 d-flex flex-row justify-space-between align-center"
      style="
        position: fixed;
        z-index: 200;
        top: 0;
        width: 98%;
        padding-top: 16px;
      "
    >
      <v-card-title class="pl-0 ml-0">
        <p
          class="breadcrumb-text-unselected underline-on-hover cursor"
          @click="goToCampaigns"
        >
          Campaigns
        </p>
        <v-icon
          class="breadcrumb-arrow-unselected"
          style="margin-top: 6px !important"
          >mdi-chevron-right</v-icon
        >
        <p
          class="breadcrumb-text-unselected underline-on-hover cursor"
          @click="goBack"
        >
          {{ campaignName }}
        </p>
        <v-icon
          class="breadcrumb-arrow-unselected"
          style="margin-top: 6px !important"
          >mdi-chevron-right</v-icon
        >
        <p class="breadcrumb-text" style="margin-top: 2px">Test Report</p>
        <v-icon class="breadcrumb-arrow" style="margin-top: 5px !important"
          >mdi-chevron-down</v-icon
        >
      </v-card-title>
    <UserNavMenu/>
    </v-card>
    <div class="title-section d-flex justify-space-between">
      <div class="text-h6 font-weight-bold">Test Report</div>
      <TestToggleComponent v-if="assessmentType?.toLowerCase() === 'dynamic'" @testChange="switchResult($event)" :buttons="isVgosTeacher ? stages.teacher : stages.js" :isSelect="isSelect"></TestToggleComponent>
    </div>
    </template>
    <v-card class="repCard" v-if="!isMobile">
      <div style="position: sticky; top: 0; z-index: 2; background: white;">
        <div class="d-flex viewTestProctoringBox mx-4 flex-1"> 
          <div class="d-flex px-2 flex-column username-et-email-box">
            <p class="font-weight-bold text-h6">{{ selectedUserName | capitalizeFirst }}</p>
            <p class="subtitle-2">{{ userEmail?.toLowerCase() }}</p>
          </div>
          
          <v-divider vertical></v-divider>

          <div class="d-flex w-100 caption">
            <div class="d-flex flex-column justify-space-between flex-1 pa-2">
              <div class="d-flex g-24 ">
                <div class="w-50 d-flex justify-space-between">
                  <span>Test Taken On</span>
                  <span>-</span>
                </div>
                <div>{{ selectedResult.testTakenOn }}</div> 
              </div>

              <div class="d-flex g-24">
                <div class="w-50 d-flex justify-space-between">
                  <span>Total Time Taken</span>
                  <span>-</span>
                </div>
                <div>{{ userTime }} mins</div> 
              </div>
            </div>
            
            <div class="d-flex flex-column justify-space-between flex-1 pa-2">
              <div class="d-flex g-24">
                <div class="w-50 d-flex justify-space-between">
                  <span>Exit Full Screen</span>
                  <span>-</span>
                </div>
                <div>{{ proctoringData["Exit Fullscreen"] }}</div>
              </div>

              <div class="d-flex g-24">
                <div class="w-50 d-flex justify-space-between">
                  <span>Screen Change</span>
                  <span>-</span>
                </div>
                <div>{{ proctoringData["Switch Tab/Browser"] }}</div>
              </div>
            </div>
            
            <div class="d-flex flex-column justify-space-between flex-1 pa-2">
              <div class="d-flex g-24">
                <div class="w-50 d-flex justify-space-between">
                  <span>Screenshot Attempted</span>
                  <span>-</span>
                </div>
                <div>{{proctoringData.Screenshot}}</div>
              </div>

              <div class="d-flex g-24">
                <div class="w-50 d-flex justify-space-between">
                  <span>Assessment Type</span>
                  <span>-</span>
                </div>
                <div>{{ assessmentType | capitalizeFirst }} Assessment</div>
              </div>
            </div>
          </div>
        </div>
        <div class="d-flex pa-2 viewTestProctoringBox mx-4 flex-1">
            <div class="d-flex g-24">
              <div class="d-flex align-center g-8 pa-0 netscore"><span class="label">Net Score -</span> <span class="score"> {{ totalScore }} / {{ maxMarks }}</span></div>
            </div>
          <div class="d-flex align-center g-16">
            <template>
              <v-menu bottom offset-x offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn class="btn outline-btn" :loading="downloadLoader" depressed outlined rounded v-bind="attrs" v-on="on">
                    <v-icon>mdi-import mdi-rotate-90</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item @click="openTestReport">
                    Test Report
                  </v-list-item>
                  <v-list-item v-if="assessmentType.toLowerCase() === 'dynamic' && isSelect === 1 && hasPsychometry" @click="generatePsychometryReport" :disabled="disablePsychometryDownload">
                    Psychometry Report
                  </v-list-item>
                  <v-list-item v-if="['userPerformanceReport', 'psychometryReport'].includes(campaignMetaData?.reportGeneration?.reportType)" @click="openAdvancedReport" :disabled="disableAdvanceReportDownload">
                    {{assessmentType.toLowerCase() === 'dynamic' ? 'Advanced Report' : 'Psychometry Report'}}
                  </v-list-item>
                  <v-list-item v-if="assessmentType.toLowerCase() === 'dynamic' && campaignMetaData?.reportGeneration?.reportType === 'gtoReport'"@click="openAdvancedReport" :disabled="disableAdvanceReportDownload">
                    GTO Report
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
            
            <v-btn class="btn outline-btn"  @click="showProc" depressed outlined rounded>Proctoring Data</v-btn>
          </div>
        </div>

        <div
          class="w-100 d-flex flex-row align-center justify-space-between py-0 my-0"
        >
          <div>
            <div
              v-if="selectedSkill.skillName == 'Psychometry' && assessmentType.toLowerCase() === 'dynamic'"
              class="d-flex flex-row align-center py-0 mb-3 mt-0"
            >
              <p class="mx-4 px-0">
                <b>
                  <span :style="{ background: getPsychometricEvaluation(selectedSkill.score,
                        selectedSkill.maximumMarks).color }">

                    {{
                      getPsychometricEvaluation(
                        selectedSkill.score,
                        selectedSkill.maximumMarks
                        ).message
                      }}
                      </span>
                  
                  </b
                >
              </p>
            </div>
          </div>
        </div>

        <v-row class="py-0 px-4 my-1 mx-0 flex-row align-center">
          <v-spacer></v-spacer>
        </v-row>
      </div>

      <div class="w-100 d-flex flex-row question-result-view px-4 pb-4">
        <v-card
          class="scroll-view core-skills-container w-18 rounded-xl"
          :height="selectedSkill.skillName == 'Psychometry' && assessmentType?.toLowerCase() === 'dynamic' ? 'calc(88vh - 320px)' : 'calc(88vh - 240px)'"
          id="myScroll"
          outlined
          elevation="0"
          style="position: sticky;"
        >
        <v-btn-toggle v-model="selectedSkillName" class="d-flex flex-column pa-4 ga-8 v-btn-toggle--group active-cls">           
          <v-card-subtitle v-if="selectedResult.skills.filter(skill=>skill.isCoreSkill).length > 0" class="pa-0 font-weight-bold">
            {{ campaignMetaData?.nameAliases?.skills["Core Skills"]?.alias }}
          </v-card-subtitle>
          <template v-for="(skill, index) in selectedResult.skills">
            <template v-if="skill.isCoreSkill">
              <v-btn
              depressed
              :color="getButtonColor(skill, index)"
              @click="getSelectedSkillData(skill, index)"
              outlined
              :class="getActiveClass(skill)"
              class="rounded-xl tabBtn mx-0"
              :key="index"
              :value="skill.skillName"
              :disabled="!skill.questions"
>
                  <div class="d-flex flex-column w-100 align-start text-capitalize">
                    <div class="d-flex justify-space-between w-100 align-center">
                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <div v-bind="attrs" v-on="on" class="text-subtitle-2 text-uppercase text--secondary">
                            {{ skill.skillName }}
                          </div>
                        </template>
                        <span>{{ skill.skillName }}</span>
                      </v-tooltip>
                      <div v-if="!campaignMetaData?.isSkillLevelScoreNormalized">
                        <div class="text-body-2 text--secondary">
                          Score
                        </div>
                        <div class="text-center text-body-2 font-weight-bold text--secondary">
                          {{ Number.isFinite(skill?.score) ? `${skill?.score || 0}/${skill?.maximumMarks}` : 'NA' }}
                        </div>
                      </div>
                    </div>
                    <div v-if="campaignMetaData?.isSkillLevelScoreNormalized" class="d-flex flex-row w-100 justify-space-between">
                      <div>
                        <div v-if="skill?.weightage" class="text-body-2 text--secondary">
                          Weightage
                        </div>
                        <div v-if="skill?.weightage" class="text-left text-body-2 font-weight-bold text--secondary">
                          {{ skill.weightage }}%
                        </div>
                      </div>
                      <div>
                        <div class="text-body-2 text--secondary">
                          Score
                        </div>
                        
                        <div class="text-left text-body-2 font-weight-bold text--secondary" v-if="isNaN(skill.score) || skill.score === null">
                          NA
                        </div>
                        <div class="text-left text-body-2 font-weight-bold text--secondary" v-else>
                          {{((skill.score / skill.maximumMarks)*10).toFixed(2) }}
                        </div>
                      </div>
                    </div>
                  </div>
                </v-btn
              >           
            </template>
          </template>
          <v-card-subtitle v-if="selectedResult.skills.filter(skill=>!skill.isCoreSkill).length > 0" class="px-0 pb-0 font-weight-bold">OTHER SKILLS</v-card-subtitle>
          <template v-for="(skill, index) in selectedResult.skills">
              <v-btn                
                depressed
                :color="getButtonColor(skill, index)"
                @click="getSelectedSkillData(skill, index)"
                outlined
                class="rounded-xl tabBtn mx-0" 
                :class="getActiveClass(skill)" 
                :key="index" :value="skill.skillName" 
                v-if="!skill.isCoreSkill"             
                >
                  <div class="d-flex flex-column w-100 align-start text-capitalize">
                    <div class="d-flex justify-space-between align-center w-100">
                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <div v-bind="attrs" v-on="on" class="text-subtitle-2 text-uppercase text--secondary">
                            {{ skill.skillName }}
                          </div>
                        </template>
                        <span>{{ skill.skillName }}</span>
                      </v-tooltip>
                      <div v-if="!campaignMetaData?.isSkillLevelScoreNormalized">
                        <div class="text-body-2 text--secondary">
                          Score
                        </div>
                        <div class="text-center text-body-2 font-weight-bold text--secondary">
                          {{ Number.isFinite(skill?.score) ? `${skill?.score || 0}/${skill?.maximumMarks}` : 'NA' }}
                        </div>
                      </div>
                    </div>
                    <div v-if="campaignMetaData?.isSkillLevelScoreNormalized" class="d-flex flex-row w-100 justify-space-between">
                      <div>
                        <div v-if="skill?.weightage" class="text-body-2 text--secondary">
                          Weightage
                        </div>
                        <div v-if="skill?.weightage" class="text-left text-body-2 font-weight-bold text--secondary">
                          {{ skill.weightage }}%
                        </div>
                      </div>
                      <div>
                        <div class="text-body-2 text--secondary">
                          Score
                        </div>
                        <div class="text-left text-body-2 font-weight-bold text--secondary">
                          {{((skill.score / skill.maximumMarks)*10).toFixed(2) }}
                        </div>
                      </div>
                    </div>
                  </div>
                </v-btn
              >           
          </template>
        </v-btn-toggle>
        </v-card>

        <v-card
          class="scroll-view w-82 px-4"
          :height="selectedSkill.skillName == 'Psychometry' && assessmentType?.toLowerCase() === 'dynamic'? 'calc(88vh - 320px)' : 'calc(88vh - 240px)'"
          id="myScroll"
          elevation="0"
        >
        <span class="skill-section-top"></span>
            <v-card class="d-flex justify-space-between align-center w-100 rounded-xl px-3 mb-2" outlined elevation="0">
                <v-card width="270px" class="elevation-0 transparent">
                  <v-tooltip bottom style="z-index: 999">
                    <template v-slot:activator="{ on, attrs }">
                      <div v-bind="attrs" v-on="on">
                        <img
                          src="../../public/svgs/CurrentUserIcon.svg"
                          alt="pass icon"
                          style="position: relative; bottom: -8px; transform: translateX(-10px)"
                          :style="`left:${progressLevel}%`"
                        />
                      </div>
                    </template>
                    <span>{{ campaignMetaData?.isSkillLevelScoreNormalized
                      ? `${((selectedSkill.score / selectedSkill.maximumMarks) * 10).toFixed(0)} / 10`
                      : `${selectedSkill.score} / ${selectedSkill.maximumMarks}` }}</span>
                  </v-tooltip>
                  <v-progress-linear
                    v-model="progressLevel"
                    rounded
                    :color="(assessmentType?.toLowerCase() === 'dynamic' && selectedSkill.result !=='PASSED') ? 'error' : 'success'"
                    height="10"
                    style="position: relative; pointer-events: none"
                  ></v-progress-linear>
                  <v-tooltip v-if="assessmentType?.toLowerCase() === 'dynamic' && campaignMetaData?.isSkillLevelPassFailApplicable" bottom style="z-index: 999">
                    <template v-slot:activator="{ on, attrs }">
                      <div v-bind="attrs" v-on="on">
                        <img
                          src="../../public/svgs/PassingIcon.svg"
                          alt="pass icon"
                          style="position: relative; transform: translateX(-10px)"
                          :style="`left:${((selectedSkill.threhold) / maxMarks)*100}%`"
                        />
                      </div>
                    </template>
                    <span>{{ selectedSkill.threhold }} / {{maxMarks}}</span>
                  </v-tooltip>
                  <div v-else :style="{height: '24px', width: '24px', visibility: 'hidden'}"></div>
                </v-card>

                <div class="d-flex flex-row align-center mx-0 px-0">
                  <!-- <img class="mx-2" src="../../public/svgs/AverageIcon.svg" alt="avg reference icon">
                    <div class="text-body-2">Average User</div> -->
                  <img v-if="assessmentType.toLowerCase() === 'dynamic' && campaignMetaData?.isSkillLevelPassFailApplicable" class="ml-6 mr-2" src="../../public/svgs/PassingIcon.svg" alt="pass reference icon" />
                  <div v-if="assessmentType.toLowerCase() === 'dynamic' && campaignMetaData?.isSkillLevelPassFailApplicable" class="text-body-2">Passing Score</div>
                  <img class="mx-2" src="../../public/svgs/CurrentUserUpIcon.svg" alt="current reference icon">
                  <div class="text-body-2">User's score</div>
                </div>
            </v-card>
            <v-card class="d-flex justify-space-between align-center w-100 rounded-xl px-6 py-4 mb-4" outlined elevation="0">
              <div class="d-flex g-16">
                <span>Time Taken</span>
                <span>-</span>
                <span>{{selectedSkill?.totalTimeTaken}} mins</span>
              </div>
              <div class="d-flex g-16">
                <span>Total Question</span>
                <span>-</span>
                <span>{{selectedSkill?.questions.length}}</span>
              </div>
            </v-card>
          <v-list>
            <v-list-item
              v-for="(question, questionIndex) in selectedSkill.questions"
              :key="questionIndex" class="pa-0"
            >
              <div class="w-100 d-flex flex-column">
                <!-- A passage based question shows its statement once, in a card, and then
                     renders its sub-questions in place of itself. -->
                <template v-if="isPassageQuestion(question)">
                  <div class="d-flex flex-row">
                    <div>Q.{{ questionIndex + 1 }}</div>
                  </div>
                  <v-card class="passage-statement-card rounded-lg pa-4 mt-2" outlined elevation="0">
                    <p role-type="subtitle-4" class="passage-statement-title">Paragraph</p>
                    <p
                      role-type="subtitle-7" class="passage-statement mt-2"
                      :class="{ 'passage-statement--plain-text': !hasHtmlMarkup(question.questionStatement || question.sanitizedQuestionStatement) }"
                      v-html="formatQuestionStatementForDisplay(question.questionStatement || question.sanitizedQuestionStatement)"
                    ></p>
                  </v-card>
                  <!-- Opening line of the sub-question group -->
                  <div class="my-3 customDivider"></div>
                </template>

                <div
                  class="w-100 d-flex flex-column"
                  v-for="(item, index) in questionsToRender(question)"
                  :key="index"
                >
                <div class="d-flex flex-row w-100">
                  <div class="d-flex flex-column flex-1">
                    <div class="d-flex flex-row">
                      <div>Q.{{ questionLabel(questionIndex, index, question) }}</div>
                      <div class="pl-4" v-html="formatQuestionStatementForDisplay(item.questionStatement || item.sanitizedQuestionStatement)"></div>
                      
                    </div>
                    <div class="pl-8 py-2" v-if="item?.questionAssetUrl && item?.questionAssetUrl !== 'NA'">
                      <v-img v-if="item.mediaType === 'IMG'" class="image-border" :width="400" @error="imgError(item)" :src="item.questionAssetUrl"/>
                      <component v-else-if="item.mediaType === 'AUDIO' || item.mediaType === 'VIDEO'" :is="item.mediaType.toLowerCase()" :src="item.questionAssetUrl" controls controlsList="nodownload noplaybackrate" class="test-report-audio"></component>
                      <object v-else-if="item.mediaType === 'OBJ'" class="image-border" :width="220" @error="objError(item)" :data="item.questionAssetUrl"></object>
                      <div v-else-if="item.mediaType === 'ERR'" outlined class="d-flex justify-center flex-column align-center error--text ">
                            <!-- Media Not Found -->
                            <img src="../assets/caution.png" alt="media not found" width="48" height="48">
                            <div class="mt-2">Media Not Found</div>
                      </div>
                      <v-img v-else class="image-border" :width="200" :src="loadingAssetURL"/>
                    </div> 
                    <v-row class="pl-8 pt-2 my-1 ml-2 d-flex gap-2">
                      <v-chip
                        v-if="
                          item.difficultyLevel != 'NA' &&
                          item.skill != 'Psychometry'
                        "
                        small
                        text-color="#3F41D1"
                        color="#CFD5F1"
                        >{{ item.difficultyLevel }}</v-chip
                      >
                      <v-chip
                        v-if="item.markingType "
                        small
                        text-color="#3F41D1"
                        color="#CFD5F1"
                        >{{ item.markingType }}
                      </v-chip
                      >
                      <v-chip
                        v-if="item.markingType != 'Likert Marking'"
                        small
                        text-color="#3F41D1"
                        color="#CFD5F1"
                        >{{ `${Number(item?.marksAllotted) || 1} Mark${ Number(item?.marksAllotted) > 1? 's' : '' }`}}
                      </v-chip>
                      <v-chip v-if="item.questionType" small text-color="#3F41D1" color="#CFD5F1">
                        {{item.questionType}}
                      </v-chip>
                      <v-chip v-if="item.level != 'NA'" small text-color="#3F41D1" color="#CFD5F1">
                        {{item.level}}
                      </v-chip>
                      <v-chip
                      v-if="item.grade"
                      small
                      text-color="#3F41D1"
                      color="#CFD5F1"
                      >{{ item.grade }}</v-chip
                      >
                      <v-chip
                      v-if="item?.strand && assessmentType.toLowerCase() === 'static'"
                      small
                      text-color="#3F41D1"
                      color="#CFD5F1"
                      >{{ item.strand }}</v-chip
                      >
                    </v-row>
                    <div class="d-flex flex-row">
                      <v-layout wrap row class="d-flex flex-column pl-9 my-4 ml-1">
                        <v-row no-gutters style="height: 16px;">
                          <v-col cols="10">{{item.questionType ==  'FILL_IN_THE_BLANKS' ? "Correct Answer" : 'Options'}}</v-col>
                          <v-col v-if="item?.markingType?.toLowerCase() === 'likert marking'" cols="2">Marks</v-col>
                        </v-row>
                        <v-row v-if="imageInOption(item.questionOptions)"
                        v-for="(option, i) in item.questionOptions"
                        :key="i" class="d-flex mx-1 px-2 flex-1">
                          <v-col cols="10" class="flex-1">
                            <v-img v-if="option.mediaType === 'IMG'" :width="200" @error="imgError(option)" :src="option.optionValue" :class="(item.correctAnswer?.includes(option.optionKey) || item?.markingType?.toLowerCase() === 'likert marking')? 'correct-answer-image':'image-border'" />
                            <object v-else-if="option.mediaType === 'OBJ'" :width="200" @error="objError(option)" :data="option.optionValue" :class="(item.correctAnswer?.includes(option.optionKey) || item?.markingType?.toLowerCase() === 'likert marking')? 'correct-answer-image':'image-border'"></object>
                            <div v-else-if="option.mediaType === 'ERR'" outlined class="d-flex justify-center flex-column align-center error--text ">                     
                                  <!-- Media Not Found -->
                                  <img src="../assets/caution.png" alt="media not found" width="48" height="48">
                                  <div class="mt-2">Media Not Found</div>
                            </div>
                            <v-img v-else class="image-border" :width="200" :src="loadingAssetURL"/>
                          </v-col>
                          <v-col v-if="item?.markingType?.toLowerCase() === 'likert marking'" cols="2" class="ml-auto pa-0 option-score pl-4">
                            <div>{{option?.optionScore}}</div>
                            <!-- <div v-else-if="item.correctAnswer?.includes(option.optionKey)" class="option-score">{{item?.marksAllotted || 1}}</div> -->
                          </v-col>
                        </v-row>
                        <v-row v-if="!imageInOption(item.questionOptions) &&   !['MATCH_THE_FOLLOWING', 'FILL_IN_THE_BLANKS'].includes(item.questionType)"
                        class="d-flex pa-0 w-100 flex-1"
                          v-for="(option, i) in item.questionOptions" :key="i">
                          <v-col cols="10" class="flex-1 py-2" v-if="item.questionType != 'MATCH_THE_FOLLOWING'"
                            :class=" 
                              (item.correctAnswer?.includes(option.optionKey) || item?.markingType?.toLowerCase() === 'likert marking')
                                ? 'pr-3 mt-1 text-body-2 success--text d-flex flex-row'
                                : 'pr-3 mt-1 text-body-2 d-flex flex-row'
                            "
                          >
                            <div class="mr-1" style="margin-right:4px;"> {{option.optionKey}}.</div>
                            <div v-html="option.optionValue"></div>
                          </v-col>
                          <v-col v-if="item?.markingType?.toLowerCase() === 'likert marking'" cols="2" class="ml-auto pa-0 option-score pl-4">
                            <div>{{option?.optionScore}}</div>
                            <!-- <div v-else-if="item.correctAnswer?.includes(option.optionKey)" class="success--text">{{item?.marksAllotted || 1}}</div> -->
                          </v-col>
                        </v-row>
                        <v-row v-if="item.questionType ==  'FILL_IN_THE_BLANKS'">
                         <v-col class="pr-3 mt-1 text-body-2 success--text d-flex flex-row">{{ item.correctAnswer }}</v-col>
                        </v-row>
                        <div
                        class="d-flex flex-column w-100"
                          v-if="item.skill !== 'Psychometry' && !imageInOption(item.questionOptions) && item.questionType ==  'MATCH_THE_FOLLOWING'" 
                        >
                        <div v-for="(item, index) in item.mtfOptions" :key="item.key" class="d-flex">
                          <span class="flex-1">{{ index + 1 }}. {{ item.key }}</span>
                          <span class="px-2 d-flex flex-1 ">{{ getAlphabet(index) }}. {{ item.value }}</span>
                        </div>
                        </div>
                      </v-layout>
                      <div v-if="item?.markingType?.toLowerCase() !== 'likert marking'" class="d-flex flex-column w-20">
                        <div>
                          Marks
                        </div>
                        <div class="mt-3 pa-0 option-score pl-4 flex-1">
                          <div class="success--text">{{item?.marksAllotted || 1}}</div>
                        </div>
                      </div>
                    </div>
                    
                    
                    <v-row class="pl-8 pt-2 ml-1" v-if="item.questionType=='MATCH_THE_FOLLOWING'">
                      <div class="pl-1 text-body-2">Answer -</div>
                      <div class="pl-1 text-subtitle-2" style="color:green">
                        {{ generateFormattedAnswer(item.mtfOptions,item.correctAnswer,item.myAnswer,false) }}
                      </div>
                    </v-row>
                  </div>

                  <div class="w-20 mt-auto">
                    <PieChart
                      :pieData="[
                        ['Type', 'Percentage'],
                        [
                          'Correct',
                          (item.noOfCorrectAttempt / item.noOfAttempt) * 100,
                        ],
                        [
                          'Incorrect',
                          item.noOfCorrectAttempt
                            ? 100 -
                              (item.noOfCorrectAttempt / item.noOfAttempt) * 100
                            : 100,
                        ],
                      ]"
                    />
                  </div>

                  <div class="w-20 mt-auto">
                    <TimeChart :timeData="getTimeChartData(item)" />
                  </div>
                </div>

                <div class="d-flex flex-column w-100 ml-1">
                  <div class="px-0 pl-9 w-100 mt-4">
                    <v-divider></v-divider>
                  </div>

                  <div class="pl-9 pt-2 text-subtitle-2 w-100 d-flex flex-row align-center">
                    <div>
                      Response Analytics -

                      <span v-if="!item.myAnswer" class="mr-6"
                        >Not Attempted</span
                      >

                      <span v-else class="mr-6">Attempted</span>
                    </div>

                    <div>Answered -</div>
                    
                    <div v-if="!item.myAnswer" class="mr-6">Skipped</div>
                    <div v-else-if="item.myAnswer && item.questionType == 'MATCH_THE_FOLLOWING'" class="mr-6 ml-2">
                      <template v-for="(answer, index) in generateFormattedAnswer(item.mtfOptions, item.correctAnswer, item.myAnswer, true)">
                        <span :style="{ color: answer.color }">
                          {{ answer.text }}{{ index < generateFormattedAnswer(item.mtfOptions, item.correctAnswer, item.myAnswer, true).length - 1 ? ',' : '' }}
                        </span>
                      </template>
                    </div>
                    <div
                      class="mr-6"
                      v-else
                      :class="item.isAnswerCorrect ? 'd-flex align-center pa-1 correct-answer' : 'd-flex align-center pa-1 incorrect-answer'">
                      <v-img class="image-border" v-if="imageInOption(item.questionOptions)" 
                      :width="200"
                      :src="returnCorrectAnswerAssetURL(item)"/>
                      <div class="pl-2">
                        {{
                          item.isAnswerCorrect
                            ? item.myAnswer + " (Correct)"
                            : item.myAnswer + " (Incorrect)"
                        }}
                      </div>
                    </div>

                    <div>Time taken -</div>
                    <div v-if="item.timeTaken != null" class="pl-1">
                      {{ item.timeTaken.toFixed(2) }} sec
                    </div>
                  </div>
                  <div class="my-3 customDivider" v-if="!isLastSubQuestion(question, index)"></div>
                </div>
                </div>
                <!-- Closing line of the sub-question group -->
                <div class="my-3 customDivider" v-if="isPassageQuestion(question)"></div>
              </div>
            </v-list-item>
          </v-list>
        </v-card>
      </div>
    </v-card>

    <v-dialog v-model="notificationDialog" class="cdz" max-width="380px">
      <Notification @close-dialog="notificationDialog = false" />
    </v-dialog>

    <v-dialog class="cdz" v-model="proctorDialog" fullscreen persistent>
      <v-card class="proctorCard" v-if="proctorDialog">
        <v-tabs color="success accent-4" left>
          <v-tab>Cumulative</v-tab>
          <v-tab>Timeline</v-tab>

          <v-tab-item :key="1">
            <v-container fluid>
              <div class="procBubChart">
                <GChart
                  v-if="proctorDialog"
                  type="BarChart"
                  style="height: 100%; width: 100%"
                  :data="cumulativeProctoringData"
                  :options="barChartOptions"
                />
              </div>
            </v-container>
          </v-tab-item>

          <v-tab-item :key="2">
            <v-container fluid>
              <div class="procBubChart">
                <GChart
                  v-if="proctorDialog"
                  type="BubbleChart"
                  style="height: 100%; width: 100%"
                  :data="bubbleChartData"
                  :options="bubbleChartOptions"
                />
              </div>
            </v-container>
          </v-tab-item>
        </v-tabs>
        <div class="w-100 d-flex justify-end px-4">
          <v-btn
            class="btn outline-btn rounded-pill elevation-0 mx-4"
            @click="proctorDialog = false"
          >
            Close
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
    <section class="not-for-screen-render" ref="psychometryReportTemplate">
        <div style="background: #F7F7F7;; font-size:14px !important; font-family: GraphikRegular; font-weight:400; page-break-inside: avoid !important; padding:16px;">
            <div style="font-size: 20px; font-family: 'GraphikMedium', sans-serif !important; font-weight:500; padding-bottom: 4px;">Psychometry Report</div>
            <div style="border: 1px solid rgba(0, 0, 0, 0.12); display: flex;  flex-direction: row; background: #FFFFFF; border-radius: 8px; padding: 4px;">
                <div style="display: flex;  flex-direction: row; width: 25%; align-items:center">
                    <v-avatar size="69" style="margin: 8px">
                        <img v-if="proctoringPic !== ''" :src="proctoringPic" alt="Report User Pic">
                          <img v-else  src="../assets/avtar.png" alt="Report User Pic"> 
                    </v-avatar>
                    <div style="display: flex;  flex-direction: column; width:100%">
                    <div style="margin-top:8px; font-size:14px; " class="reportTableText"> {{ userName | capitalizeFirst }} </div>
                    <div style="margin-top:8px; white-space:wrap; text-transform:lowercase"> {{ userEmail }} </div>
                    <div style="margin-top:8px;"> {{ userPhone }} </div>
                    </div>
                </div>
                <v-divider vertical></v-divider>
                <div style="width: 35%; padding:16px">
                    <v-row>
                    <v-col cols="3" style="padding-right: 0px; padding-left:16px">
                        Level -
                    </v-col>
                    <v-col cols="9" style="padding: 4px 0px;">
                        <v-chip style="font-size:14px" v-for="(level, key) in levels" :key="key">{{ campaignMetaData?.nameAliases?.levels?.[level] ?? level }}</v-chip>
                    </v-col>
                    </v-row>
                    <v-row>
                    <v-col cols="3" style="padding: 8px 0px 8px 16px;">
                        Subjects -
                    </v-col>
                    <v-col cols="9" style="display: flex;  flex-direction: row; padding: 0px;">
                        <v-chip
                        v-for="(subject, index) in subjects"
                        :key="index" style="font-size:14px; margin-right:2px;">
                        {{ subject }}
                        </v-chip>
                    </v-col>
                    </v-row>
                </div>   
                <v-divider vertical></v-divider>      
                <div style="width: 20%; display: flex;  flex-direction: column; justify-content: space-around; padding: 0px 16px">
                    <div style="display: flex;  flex-direction: row; justify-content: space-between; padding-top: 4px;">
                    <div style="font-weight:400;">
                        Total Experience-
                    </div>
                    <div class="reportTableText" style="font-size:14px; white-space:nowrap;">
                        {{interviewDetails?.candidateDetails?.totalExperience?.years !== undefined?
                            `${interviewDetails?.candidateDetails?.totalExperience?.years} yrs` : "" }}  
                        {{ interviewDetails?.candidateDetails?.totalExperience?.months !== undefined?
                            `${interviewDetails?.candidateDetails?.totalExperience?.months} mo` : "-"}}
                    </div>
                    </div>
                    <div style="display: flex !important; flex-direction: row flex !important; justify-content: space-between; padding-top: 4px;">
                    <div style="font-weight:400;">
                        Relevant Experience-
                    </div>
                    <div class="reportTableText" style="font-size:14px; white-space:nowrap;">
                        {{interviewDetails?.candidateDetails?.relevantExperience?.years !== undefined?
                            `${interviewDetails?.candidateDetails?.relevantExperience?.years} yrs` : "" }}
                        {{ interviewDetails?.candidateDetails?.relevantExperience?.months !== undefined?
                            `${interviewDetails?.candidateDetails?.relevantExperience?.months} mo` : "-"}}
                    </div>
                    </div>
                    <div style="display: flex !important;  flex-direction: row !important; justify-content: space-between; padding-top: 4px;">
                    <div style="font-weight:400;">
                        Notice Period-
                    </div>
                    <div class="reportTableText" style="font-size:14px; white-space:nowrap;">
                        {{interviewDetails?.candidateDetails?.noticePeriod !== undefined?
                            interviewDetails?.candidateDetails?.noticePeriod : "-"}}
                    </div>
                    </div>
                </div>
                <v-divider vertical></v-divider>      
                <div style="width: 20%; display: flex;  flex-direction: column; justify-content: space-around; padding: 0px 16px">
                    <div style="display: flex;  flex-direction: row; justify-content: space-between;">
                    <div style="font-weight:400;">
                        Current CTC-
                    </div>
                    <div class="reportTableText" style="font-size:14px; white-space:nowrap;">
                        {{interviewDetails?.candidateDetails?.currentCTC !== undefined?
                            `${interviewDetails?.candidateDetails?.currentCTC} LPA` : "-"}}
                    </div>
                    </div>
                    <div style="display: flex;  flex-direction: row; justify-content: space-between;">
                    <div style="font-weight:400;">
                        Expected CTC-
                    </div>
                    <div class="reportTableText" style="font-size:14px; white-space:nowrap;">
                        {{interviewDetails?.candidateDetails?.expectedCTC !== undefined?
                            `${interviewDetails?.candidateDetails?.expectedCTC} LPA` : "-"}}
                    </div>
                    </div>
            </div>
            </div>
            <div style="background: #FFFFFF; border-radius: 8px; margin-top:16px; padding: 12px 16px;">
              <div style="display:flex; gap: 32px;">
                <div style="display: flex; align-items: center; gap: 8px">
                  <div style="width: 24px; height: 16px; border-radius: 4px; background-color: #06C270;"></div>
                  <div>Highly recommended</div>
                </div>
                <div style="display: flex; align-items: center; align-items: center; gap: 8px;">
                  <div style="width: 24px; height: 16px; border-radius: 4px; background-color: #FADC00;"></div>
                  <div>Recommended</div>
                </div>
                <div style="display: flex; align-items: center; gap: 8px;">
                  <div style="width: 24px; height: 16px; border-radius: 4px; background-color: #FB7500;"></div>
                  <div>Development needed</div>
                </div>
                <div style="display: flex; align-items: center; gap: 8px;">
                  <div style="width: 24px; height: 16px; border-radius: 4px; background-color: #D40028;"></div>
                  <div>Not recommended at this time</div>
                </div>
              </div>
            </div>
            <div style="background: #FFFFFF; border-radius: 8px; margin-top:16px; padding:8px;">
            <div style="font-size: 16px; font-family: 'GraphikMedium', sans-serif !important; font-weight:500; padding-bottom:4px">
                Self-awareness
            </div>
            <div :class="selectBackgroundColor(normalizedStrandsScore?.selfAwareness?.score)" style="display: flex;  flex-direction: row; border-radius: 8px; padding:4px;">
                <div class="reportTableText" style="display: flex;  flex-direction: column; justify-content: center; gap: 8px; width: 25%; white-space: nowrap; padding:0px 12px; font-size:14px;">
                  <div>
                      Self Motivation
                  </div>
                  <div>
                      Self-concept
                  </div>
                </div>
                <v-divider vertical></v-divider> 
                <div style="display: flex;  flex-direction: row; align-items: center; margin-left:auto; padding: 0px 16px">
                <div v-for="rate in 4":key="rate"
                    :class="selectRating(rate, normalizedStrandsScore?.selfAwareness?.score)">
                    {{ rate }}
                </div>
                </div>
                <div style="display: flex;  flex-direction: column; width: 50%; margin-left: auto">
                <div style="padding: 4px 4px; font-size: 14px; font-family: GraphikRegular; font-weight:400;">Feedback</div>
                <div style="background: #FFFFFF; border-radius: 8px; font-size: 14px; font-family: GraphikRegular; font-weight:400; color: #00000099; padding: 8px; margin-bottom:8px; height:100%; width:98%; border: 1px solid #0000002E;">
                    {{ normalizedStrandsScore?.selfAwareness?.feedback }} 
                </div>
                </div>
            </div>
            </div>
      
            <div class="mt-2 pa-2" style="background-color: #FFFFFF; border-radius: 8px; margin-top:16px; padding:8px !important;">
            <div style="font-size: 16px; font-family: 'GraphikMedium', sans-serif !important; font-weight:500; padding-bottom:4px !important">
                Social Awareness
            </div>
            <div :class="selectBackgroundColor(normalizedStrandsScore?.socialAwareness?.score)" style="display: flex;  flex-direction: row; border-radius: 8px; padding:4px">
                <div class="reportTableText" style="display: flex;  flex-direction: column; justify-content: center; gap: 8px; width: 25%; white-space: nowrap; padding:0px 12px; font-size:14px">
                  <div>
                      Adaptability
                  </div>
                  <div>
                      Behavior Monitoring 
                  </div>
                  <div>
                      Emotional Regulation
                  </div>
                </div>
                <v-divider vertical></v-divider> 
                <div style="display: flex;  flex-direction: row; align-items: center; margin-left: auto; padding: 0px 16px">
                <div v-for="rate in 4":key="rate"
                    :class="selectRating(rate, normalizedStrandsScore?.socialAwareness?.score)">
                    {{ rate }}
                </div>
                </div>
                <div style="display: flex;  flex-direction: column; width: 50%; margin-left:auto;">
                <div style="padding: 4px 4px; font-size: 14px; font-family: GraphikRegular; font-weight:400;">Feedback</div>
                <div style="background: #FFFFFF; border-radius: 8px; font-size: 14px; font-family: GraphikRegular; font-weight:400; color: #00000099; padding: 8px; margin-bottom:8px; height:100%; width:98%; border: 1px solid #0000002E;">
                    {{ normalizedStrandsScore?.socialAwareness?.feedback }}
                </div>
                </div>
            </div>
            </div>
      
            <div style="background-color: #FFFFFF; border-radius: 8px; margin-top:16px; padding:8px !important;">
            <div style="font-size: 16px; font-family: 'GraphikMedium', sans-serif !important; font-weight:500; padding-bottom: 4px !important">
                Self Management
            </div>
            <div :class="selectBackgroundColor(normalizedStrandsScore?.selfManagement?.score)" style="display: flex;  flex-direction: row; border-radius: 8px; padding:4px;">
                <div class="reportTableText" style="display: flex;  flex-direction: column; justify-content: center; gap: 8px; width: 25%; white-space: nowrap; padding:0px 12px; font-size:14px">
                <div>
                    Organizational Awareness
                </div>
                <div>
                    Emotional Perception
                </div>
                <div>
                    Empathetic Concern 
                </div>
                </div>
                <v-divider vertical></v-divider> 
                <div style="display: flex;  flex-direction: row; align-items: center; margin-left:auto; padding: 0px 16px">
                <div v-for="rate in 4":key="rate"
                    :class="selectRating(rate, normalizedStrandsScore?.selfManagement?.score)">
                    {{ rate }}
                </div>
                </div>
                <div style="display: flex;  flex-direction: column; width: 50%; margin-left:auto">
                <div style="padding: 4px 4px; font-size: 14px; font-family: GraphikRegular; font-weight:400;">Feedback</div>
                <div style="background: #FFFFFF; border-radius: 8px; font-size: 14px; font-family: GraphikRegular; font-weight:400; color: #00000099; padding: 8px; margin-bottom:8px; height:100%; width:98%; border: 1px solid #0000002E;">
                    {{ normalizedStrandsScore?.selfManagement?.feedback }}
                </div>
                </div>
            </div>
            </div>
      
            <div style="background-color: #FFFFFF; border-radius: 8px; padding:8px; margin-top:16px;">
            <div style="font-size: 16px; font-family: 'GraphikMedium', sans-serif !important; font-weight:500; padding-bottom:4px;">
                Responsible decision making
            </div>
            <div :class="selectBackgroundColor(normalizedStrandsScore?.responsibleDecisionMaking?.score)" style="display: flex;  flex-direction: row; border-radius:8px; padding:4px">
                <div class="reportTableText" style="display: flex;  flex-direction: column; justify-content: center; gap: 8px; white-space: nowrap; width: 25%; padding:0px 12px; font-size:14px">
                <div>
                    Ethical Dilemma
                </div>
                <div>
                    Reasoning Skills
                </div>
                <div>
                    Critical thinking
                </div>
                </div>
                <v-divider vertical></v-divider> 
                <div style="display: flex;  flex-direction: row; align-items: center; margin-left: auto; padding: 0px 16px">
                <div v-for="rate in 4":key="rate"
                    :class="selectRating(rate, normalizedStrandsScore?.responsibleDecisionMaking?.score)">
                    {{ rate }}
                </div>
                </div>
                <div style="display: flex;  flex-direction: column; width: 50%; margin-left:auto">
                <div style="padding: 4px 4px; font-size: 14px; font-family: GraphikRegular; font-weight:400;">Feedback</div>
                <div style="background: #FFFFFF; border-radius: 8px; font-size: 14px; font-family: GraphikRegular; font-weight:400; color: #00000099; padding: 8px; margin-bottom:8px; height:100%; width:98%; border: 1px solid #0000002E;">
                    {{ normalizedStrandsScore?.responsibleDecisionMaking?.feedback }}
                </div>
                </div>
            </div>
            </div>
      
            <div style="background-color: #FFFFFF; border-radius: 8px; margin-top:16px; padding:8px"> 
            <div style="font-size: 16px; font-family: 'GraphikMedium', sans-serif !important; font-weight:500; padding-bottom:4px;">
                Relationship Skills
            </div>
            <div :class="selectBackgroundColor(normalizedStrandsScore?.relationshipSkills?.score)" style="display: flex;  flex-direction: row; align-items: center; border-radius:8px; padding:4px">
                <div class="reportTableText" style="display: flex; flex-direction: column; justify-content: center; gap: 8px; width: 25%; white-space: nowrap; padding:0px 12px; font-size:14px">
                <div>
                    Collaboration
                </div>
                <div>
                    Emotional Maturity
                </div>
                <div>
                    Interpersonal Skills
                </div>
                <div >
                    Intrapersonal Skills
                </div>
                </div>
                <v-divider vertical></v-divider> 
                <div style="display: flex;  flex-direction: row; margin-left:auto; padding: 16px;">
                <div v-for="rate in 4":key="rate"
                    :class="selectRating(rate, normalizedStrandsScore?.relationshipSkills?.score)">
                    {{ rate }}
                </div>
                </div>
                <div style="display: flex;  flex-direction: column; width: 50%; margin-left:auto;">
                <div style="padding: 4px 4px; font-size: 14px; font-family: GraphikRegular; font-weight:400;">Feedback</div>
                <div style="background: #FFFFFF; border-radius: 8px; font-size: 14px; font-family: GraphikRegular; font-weight:400; color: #00000099; padding: 8px; margin-bottom:8px; height:100%; width:98%; border: 1px solid #0000002E;">
                    {{ normalizedStrandsScore?.relationshipSkills?.feedback }}
                </div>
                </div>
            </div>
            </div>
      
            <div style="background-color: #FFFFFF; border-radius: 8px; margin-top: 16px; padding: 8px;">
            <div style="font-size: 16px; font-family: 'GraphikMedium', sans-serif !important; font-weight:500; padding-bottom:4px;">
                Personality
            </div>
            <div :class="selectBackgroundColor(normalizedStrandsScore?.personality?.score)" style="display: flex;  flex-direction: row;  border-radius:8px; padding:4px;">
                <div class="reportTableText" style="display: flex; flex-direction: column; gap: 8px; width: 25%; white-space: nowrap; padding:0px 12px;; font-size:14px">
                <div>
                    Conscientiousness
                </div>
                <div>
                    Emotional Stability
                </div>
                <div>
                    Extraversion
                </div>
                <div>
                    Openness
                </div>
                <div>
                    Teacher Effectiveness
                </div>
                </div>
                <v-divider vertical></v-divider> 
                <div style="display: flex;  flex-direction: row; align-items: center; margin-left:auto; padding:16px">
                <div v-for="rate in 4":key="rate"
                    :class="selectRating(rate, normalizedStrandsScore?.personality?.score)">
                    {{ rate }}
                </div>
                </div>
                <div style="display: flex;  flex-direction: column; width: 50%; margin-left:auto">
                <div style="padding: 4px 4px; font-size: 14px; font-family: GraphikRegular; font-weight:400;">Feedback</div>
                <div style="background: #FFFFFF; border-radius: 8px; font-size: 14px; font-family: GraphikRegular; font-weight:400; color: #00000099; padding: 8px; margin-bottom:8px; height:100%; width:98%; border: 1px solid #0000002E;">
                    {{ normalizedStrandsScore?.personality?.feedback }}
                </div>
                </div>
            </div>
            </div>
        </div>
    </section>

      <TestReportGenerator v-if="shouldMountReportsGenerator" @generation-completed="onGenerationCompleted" @generation-failed="onGenerationFailed"
        :report-contents="selectedResultForReport"
        :campaignMetaData="campaignMetaData"
        :selectedResult="selectedResult"
        :assessment-type="assessmentType" :target-user-id="targetUserId"
        :campaign-id="campaignId" :test-type="selectedResult.testType || 'MAINS'">
      </TestReportGenerator>
 </div>
</template>
<script>
import AuthService from "../services/AuthService";
import Notification from "./Notification";
import CampaignController from "@/controllers/CampaignController";
import AssessmentController from "@/controllers/AssessmentController";
import StaticAssessmentController from "@/controllers/StaticAssessmentController";
import { GChart } from "vue-google-charts/legacy";
import html2pdf from "html2pdf.js";
import PieChart from "../components/ReportGraphs/PieChart.vue";
import TimeChart from "../components/ReportGraphs/TimeChart.vue";
import { collection, doc, getDoc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase.js";
import { liveListenerUnsubscriber } from "@/mixins/liveListenerUnsubcriber";
import { passageQuestion } from "@/mixins/passageQuestion";
import VueApexCharts from "vue-apexcharts";
import { cloneDeep } from "lodash";
import BubbleLoader from "@/components/BubbleLoader.vue";
import TestReportGenerator from "@/components/ReportGenerators/TestReportGenerator.vue"
import TestToggleComponent from "@/components/ReportComponents/TestToggleComponent.vue";
import UserNavMenu from "@/components/UserNavMenu.vue";


export default {
  name:'TestReport',
  components: {
    GChart,
    PieChart,
    TimeChart,
    Notification,
    VueApexCharts,
    BubbleLoader,
    TestReportGenerator,
    TestToggleComponent,
    UserNavMenu
  },
  mixins: [ liveListenerUnsubscriber, passageQuestion ],
  data() {
    return {
      shouldMountReportsGenerator: false,
      currentDate: '',
      totalPages: 0,
      userInfo: {},
      answerProfile: [],
      scoreProfile: {},
      skillsAnalysis: [],
      recommendationLinks: [],
      scoreDistributionChartData: [
        {
          name: 'Screening/Mains Score',
          data: [],
        },
      ],
      scoreDistributionChartOptions: {
        chart: {
          height: 350,
          type: 'radar',
          toolbar: {
            show: false,
          },
        },
        title: {
          text: 'Skillset Distribution',
        },
        xaxis: {
          categories: [],
          labels: {
            show: true,
            style: {
              colors: ["#000000DE","#000000DE","#000000DE","#000000DE","#000000DE","#000000DE"],
              fontSize: "12px",
            }
          }
        },
        stroke: {
          show: true,
          width: 2,
          colors: ["#06C270"],
          dashArray: 0,
          lineCap: 'butt',
        },
        markers: {
          size:0,
        },
        fill: {
          color: ["#06C270"],
          opacity: 0.20,
        },
        dataLabels: {
          enabled: true,
          background:{
            enabled: false,
          },
          style: {
            colors: ["#212121"],
            fontWeight: 400,
          },
        },
        yaxis: {
          max: 10,
          min: 0,
          tickAmount: 5,
          labels: {
            show: true,
            formatter: function(val) {
              return val
            },
            style: {
              colors: "#666666",
              backgroundColor: "#FBDDC2",
            },
          }
        },
      },
      overallAssessmentChartData: [], // [Total Questions, Attempted, Correct]
      overallAssessmentChartOptions: {
        chart: {
          type: "radialBar",
        },
        plotOptions: {
          radialBar: {
            hollow: {
              size: "20%", // Adjust the inner circle size
            },
            track: {
              background: "#e7e7e7", // Color for the unfilled track
              strokeWidth: "100%",
            },
            dataLabels: {
              show: true, // Enable data labels
              name: {
                show: false, // Hide names (e.g., "Correct", "Attempted")
              },
              value: {
                show: true, // Always show percentage values
                fontSize: "14px", // Adjust font size for better visibility
                formatter: function (val) {
                  return `${val}%`; // Display the percentage
                },
              },
            },
          },
        },
        colors: ["#1B72E8", "#B27CC4", "#06C270"], // Colors for Total, Attempted, Correct
        labels: ["Total Questions", "Attempted", "Correct"],
        legend: {
          show: true,
          position: "right", // Legend aligned to the right
          horizontalAlign: "center",
          fontSize: '14px',
          markers: {
            shape: "square",
          },
        },
      },
      donutChartOptions: {
        chart: {
          type: 'donut',
          height: 200,
        },
        plotOptions: {
          pie: {
            donut: {
              size: '55%',
              offsetX: -30,
              hollow: {
                size: '45%',
              },
              labels: {
                show: true,
              },
            },
          },
        },
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            return val.toFixed(2) + "%";
          },
          style: {
            fontSize: '14px',
            fontWeight: 400,
            colors: ['#212121']
          },
          dropShadow: {
            enabled: false
          }
        },
        legend: {
          show: true,
          position: 'right',
          fontSize: '14px',
          fontWeight: 400,
          horizontalAlign: 'center',
          markers: {
            width: 20,
            height: 12,
            shape: 'square',
            radius: 1,
          },
          labels: {
            colors: '#212121',
            useSeriesColors: false
          },
          itemMargin: {
            horizontal: 10,
            vertical: 5
          }
        },
        stroke: {
          show: false
        },
        colors: ['#06C270', '#F77CAE'],
        labels: ['Strengths', 'Areas of Improvement'],
      },
      skillTitleCardMap:{
        'Core Skills': {
          skillName: 'Core Skills',
          text:'Core skills are essential abilities for teaching, that reflect a strong understanding and mastery of the teaching subject.',
          backgroundColor: 'core-skills-background',
          borderGradient: 'core-skills-card-border',
          isTitleCard: true,
        },
        'Communication Skills': {
          skillName: 'Communication Skills',
          text: 'Communication skills involve the ability to explain concepts clearly, listen actively, provide feedback, and create an engaging environment for learning.',
          backgroundColor: 'communication-skills-background',
          borderGradient: 'communication-skills-card-border',
          isTitleCard: true,
        },
        'Digital Literacy': {
          skillName: 'Digital Literacy',
          text: 'Digital literacy in teaching is the ability to use technology effectively to enhance learning and support students in using digital tools responsibly.',
          backgroundColor: 'digital-literacy-background',
          borderGradient: 'digital-literacy-card-border',
          isTitleCard: true,
        },
        'Pedagogy':{
          skillName: 'Pedagogy',
          text: 'Pedagogy refers to the art and science of teaching, encompassing the methods, strategies, and practices used to deliver knowledge, foster understanding, and promote effective learning among students.',
          backgroundColor: 'pedagogy-background',
          borderGradient: 'pedagogy-card-border',
          isTitleCard: true,
        },
        'Psychometry':{
          skillName: 'Psychometry',
          text: 'Psychometry uses psychological assessments to measure an individual’s abilities, traits, and behavior, helping inform decision-making and personal development.',
          backgroundColor: 'psychometry-background',
          borderGradient: 'psychometry-card-border',
          isTitleCard: true,
        }
      },
      allSections:[],
      skillsSections: [],
      subjectsSubSection:[],
      isVgosTeacher:false,
      subjects:[],
      levels: [],
      userEmail: '',
      userPhone: '',
      normalizedStrandsScore: {},
      proctoringPic: "",
      psychometryScoreMap : new Map([
                ["Self-Management", new Map([
                    [this.withinScoreRange(0, 7), {score: 1, feedback:"Likely to need significant assistance with adaptability, behavior monitoring, and emotional regulation. Further assessment needed if due to unattempted questions."}],
                    [this.withinScoreRange(8,15), {score: 2, feedback:"Likely to demonstrate some adaptability, behavior monitoring, and emotional regulation skills, with room for improvement."}],
                    [this.withinScoreRange(16,21), {score: 3, feedback:"Likely to show proficiency in adapting to change, consistent behavior monitoring, and effective emotional regulation."}],
                    [this.withinScoreRange(22,25), {score: 4, feedback:"Likely to demonstrate exceptional adaptability, effective behavior monitoring, and strong emotional regulation skills."}]
                ])],
                ["Relationship Skills", new Map([
                    [this.withinScoreRange(0,7), {score: 1, feedback:"Likely to need significant assistance in building effective relationships, engaging in collaborative efforts, and demonstrating emotional maturity. Further assessment needed if due to unattempted questions."}],
                    [this.withinScoreRange(8,15), {score: 2, feedback:"Likely to demonstrate some ability in collaboration, interpersonal skills, and emotional maturity, with inconsistent application."}],
                    [this.withinScoreRange(16,21), {score: 3, feedback:"Likely to show proficiency in collaboration, interpersonal skills, and emotional maturity, with potential for further growth."}],
                    [this.withinScoreRange(22,25), {score: 4, feedback:"Likely to build and maintain effective collaborations, demonstrate emotional maturity, and engage positively in interpersonal dynamics."}]
                ])],
                ["Social Awareness", new Map([
                    [this.withinScoreRange(0,9), {score: 1, feedback:"Likely to need significant assistance with organizational awareness, emotional perception, and empathy. Further assessment needed if due to unattempted questions."}],
                    [this.withinScoreRange(10,19), {score: 2, feedback:"Likely to demonstrate basic organizational awareness, emotional perception, and empathy, with inconsistencies in application."}],
                    [this.withinScoreRange(20,25), {score: 3, feedback:"Likely to show good organizational awareness, emotional perception, and empathetic concern, fostering positive interactions."}],
                    [this.withinScoreRange(26,30), {score: 4, feedback: "Likely to display exceptional organizational awareness, accurate emotional perception, and genuine empathetic concern."}]
                ])],
                ["Responsible decision making", new Map([
                    [this.withinScoreRange(0,5), {score: 1, feedback:"Likely to need significant assistance with ethical reasoning, reasoning skills, and critical thinking. Further assessment needed if due to unattempted questions."}],
                    [this.withinScoreRange(6,11), {score: 2, feedback:"Likely to show potential in ethical reasoning, reasoning skills, and critical thinking, but application is inconsistent in complexity."}],
                    [this.withinScoreRange(12,16), {score: 3, feedback:"Likely to demonstrate proficiency in ethical decision-making, reasoning, and critical thinking, with occasional need for further analysis."}],
                    [this.withinScoreRange(17,20), {score:4, feedback:"Likely to consistently apply ethical reasoning, critical thinking, and effective decision-making skills in complex situations."}]
                ])],
                ["Personality", new Map([
                    [this.withinScoreRange(0, 7), {score: 1, feedback:"Likely to need significant assistance with developing personality traits that support effective interactions and performance. Further assessment needed if due to unattempted questions."}],
                    [this.withinScoreRange(8,15), {score:2 , feedback:"Likely to show potential in exhibiting positive personality traits, but consistency and depth of application vary."}],
                    [this.withinScoreRange(16,21), {score: 3, feedback:"Likely to demonstrate positive personality traits that support effective interactions and performance, with areas for enhancement."}],
                    [this.withinScoreRange(22,25), {score:4, feedback:"Likely to exhibit conscientiousness, emotional stability, extraversion, openness to experience, and positively influence interactions."}]
                ])],
                ["Self-Awareness", new Map([
                    [this.withinScoreRange(0,9), {score: 1, feedback: "Likely to demonstrate limited self-awareness and understanding of personal emotions, impacting overall self-awareness growth. Further assessment needed if due to unattempted questions."}],
                    [this.withinScoreRange(10,19), {score: 2, feedback: "Likely to display basic self-awareness and understanding of personal emotions, with potential for further development."}],
                    [this.withinScoreRange(20,25), {score: 3, feedback: "Likely to show strong self-motivation, awareness of strengths and weaknesses, and consistent pursuit of personal goals."}],
                    [this.withinScoreRange(26,30), {score: 4, feedback: "Likely to demonstrate proactive initiative, optimism, and a deep understanding of self-concept and emotions."}]
                ])]
            ]),
      interviewDetails: null,
      preloader: true,
      userData: {},
      currenrSkillIndex: 0,
      selectedUserName: "",
      totalScore: 0,
      downloadLoader:false,
      userPerformanceReportDownloadLoader: false,
      downloadPsychometryLoader:false,
      disablePsychometryDownload:false,
      progressLevel: 0,
      mobileCandidateCardExpanded: true,
      userTime: 0,
      maxTime: 0,
      maxMarks: 10,
      campName: "",
      audienceId: null,
      userId: null,
      targetUserId: '',
      campaignId: null,
      studentId: '', 
      grade: '',
      campaignName: '',
      campaignType: '', 
      assessmentId: '',
      prefferedLanguage: '',
      skills: [],
      notificationDialog: false,
      isSelect:0,
      selectedSkill: {},
      userName: "",
      selectedResult: {},
      proctoringData: {
        "Copy/Paste": 0,
        "Exit Fullscreen": 0,
        Screenshot: 0,
        "Switch Tab/Browser": 0,
      },
      selectedUser: "",
      proctorDialog: false,
      userProctoringActivities: [],
      cumulativeProctoringData: [["Activity", "Count", { role: "style" }]],
      selectedUserName: "",
      dataDemoVideos: {},
      campaignStages:{},
      bubbleChartOptions: {
        title: "",

        hAxis: {
          title: "Time (min)",
          format: 0,
          viewWindow: {
            min: 0,
            max: 10,
          },
        },
        vAxis: {
          title: "Ques",
          format: 0,
          viewWindow: {
            min: 0,
            max: 10,
          },
        },

        sizeAxis: {
          minSize: 5,
          maxSize: 5,
        },

        // legend: 'none',
      },

      bubbleChartData: [
        ["Id", "Time (min)", "Ques", "Activity", "Time Stamp"],
        // ["Screenshot", 15, 1, "03:15:47"],
        // ["Screenshot", 20, 2, "03:16:47"],
        // ["Screenshot", 25, 3, "03:17:47"],
        // ["Screenshot", 30, 4, "03:18:47"],
      ],

      // barChartData: this.cumulativeProctoringData,

      barChartOptions: {
        title: "",
        titleTextStyle: {
          fontSize: 12,
        },
        hAxis: {
          title: "Count",
          format: 0,
          gridlines: {
            color: "transparent",
          },
          viewWindow: {
            min: 0,
          },
        },
        vAxis: {
          title: "Activity",
        },
        legend: "none",
      },
      selectedSkillName: null,
      hasMains: false,
      hasDemo: false,
      hasInterview: false,
      userType: "",
      leagueData:[],
      userReportData:{},
      resultData:[],
      crownColorMap: {
                gold: 'rgba(219, 212, 36, 1)',
                silver: 'rgb(192, 192, 192)',
                bronze: 'rgba(174, 173, 145, 1)',
                bonze: 'rgba(174, 173, 145, 1)'
            },
      firstTestName:'SCREENING',
      actualAnswer:null,
      userAnswer:null,
      isTestReportReady: false,
      campaignName: '',
      stages: {js:[{status:'SCREENING', disable:true, }, {status:'MAINS', disable: true,}, {status: 'DEMO', disable: true}, {status: 'INTERVIEW', disable:true}], 
            teacher:[{status:'MAINS', disable: true}]
      },
      disableAdvanceReportDownload: true,
      disableGtoReportDownload: true,
      advancedReportURL: "",
      testReportURL: "",
      selectedResultForReport:{},
      loadingAssetURL: new URL(`../assets/loading.gif`, import.meta.url).href,
      campaignMetaData: {},
      reportType: "",
      numberOfRounds:[],
      currentTestRoundIndex: 0
    };
  },
  watch: {
    search(newValue) {
      console.log(newValue);
      if (newValue == "" || newValue == null) {
        this.users = this.calculateUsersForLeague(this.preSearchUser);
      } else {
        this.searchData(newValue);
      }
    },

  },
  computed:{
     hasPsychometry() {
      return this.selectedResult?.skills?.some(
        skill => skill.skillName === 'Psychometry'
      );
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    mobileStageButtons() {
      if (this.isVgosTeacher) {
        return this.stages.teacher;
      }
      const list = this.stages.js.map((stage) => ({ ...stage }));
      if (this.numberOfRounds.length > 0) {
        this.numberOfRounds.forEach((item, index) => {
          if (index < this.currentTestRoundIndex) {
            const stage = list.find((s) => s.status === item.toUpperCase());
            if (stage) stage.disable = false;
          }
        });
      } else {
        this.resultData.forEach((item, index) => {
          if (list[index]) list[index].disable = false;
        });
      }
      return list;
    }
  },
  async mounted() {
    // Vuetify's app shell (.v-application--wrap) enforces min-height:100vh so short
    // pages still fill the screen — on mobile, this report's content is often shorter
    // than one screen, leaving a large blank gap below it. Scope the override to this
    // page only via a body class, same pattern as body-scroll-lock elsewhere.
    if (this.isMobile) {
      document.body.classList.add('mobile-test-report-page');
    }
  },
  beforeDestroy() {
    document.body.classList.remove('mobile-test-report-page');
  },
  async created() {
    this.getParams();
    await this.getCampaignInfo(this.campaignId);
    if(this.assessmentType.toLowerCase() ===  'dynamic'){
       await this.getReportData();
      await this.getResult();   
      this.isVgosTeacher ? await this.getProcDetailsForTeacher("MAINS"): "";
    }
    else if(this.assessmentType.toLowerCase() ===  'static'){
      this.fetchUserReport(this.studentId, this.campaignId, this.grade, this.assessmentId);
    }
  },
  methods: {
    formatQuestionStatementForDisplay(statement) {
      if (!statement) return statement;
      return statement.replace(/\{\s*blank\s*\}/gi, "__________");
    },
    // Sub-questions carry no attempt metrics of their own, so a missing time reads as 0
    // instead of NaN in the chart.
    getTimeChartData(question) {
      const toSeconds = (time) => (Number.isFinite(Number(time)) ? Number(Number(time).toFixed(2)) : 0);
      const averageTime = toSeconds(question?.averageTimeTaken);
      const userTime = toSeconds(question?.timeTaken);
      return [
        ["Type", "Time", { role: "style" }, { role: "annotation" }],
        ["Average User", averageTime, "#AA69CE", `${averageTime} sec`],
        ["Current User", userTime, "#9F84AE", `${userTime} sec`],
      ];
    },
    // A passage based question is never answered itself, only its sub-questions are.
    setAnswerCorrectness(question) {
      if (!question) return;
      if (question.myAnswer) {
        this.$set(
          question,
          "isAnswerCorrect",
          question.questionType == "FILL_IN_THE_BLANKS"
            ? this.evaluateFillInTheBlanksAnswers(question.myAnswer, question.correctAnswer, question.isCaseSensitive)
            : this.isCorrectResponse(question.myAnswer, question.correctAnswer, question?.markingType)
        );
      }
      question.subQuestions?.forEach((subQuestion) => this.setAnswerCorrectness(subQuestion));
    },
    getButtonColor(skill, index) {
    // Check if the selected skill matches the current index
      if(!this.campaignMetaData?.isSkillLevelPassFailApplicable){
        return 'var(--gray-04)';
      }
      else if (skill.result === 'PASSED') {
        return 'success';
      } else if (skill.result === 'FAILED') {
        
        return 'error';
      } else {
        return '';  // Return default color if result is not 'PASSED' or 'FAILED'
      }
  }, 
  getActiveClass(skill){
    if(this.assessmentType.toLowerCase() === 'static'){
      return 'default-assessment-skill-card';
    }
    else if(skill.skillName == this.selectedSkill.skillName){
      if(!this.campaignMetaData?.isSkillLevelPassFailApplicable){
        return 'default-assessment-skill-card';
      }else if (skill.result === 'PASSED' ) {
        return 'success-skill-card';
      } else if (skill.result === 'FAILED') {
        return 'error-skill-card';
      } else{
        return ''
      }
    } else if (!skill.questions) {
      return 'default-skill-card';  // Return default color if result is not 'PASSED' or 'FAILED'
      }else {
        return ''
      }

  },
generateFormattedAnswer(mtfOptions, correctAnswer, myAnswer,returnWithColor) {
    // Parse correct answers into a map
    
    const correctAnswerMap = new Map(
        correctAnswer.split(', ').map(pair => pair.split(':'))
      );

      // First, create a mapping of each option to its letter
      const letterMapping = {};
      mtfOptions.forEach((option, index) => {
        const letter = String.fromCharCode(97 + index); // a, b, c, d
        letterMapping[option.value] = letter;
      });

      // Generate actual answer using correct pairs
      const actualAnswer = [];
      Array.from(correctAnswerMap.entries()).forEach((entry, index) => {
        const number = index + 1;
        const correctCapital = entry[1];
        const letter = letterMapping[correctCapital];
        actualAnswer.push(`${number}-${letter}`);
      });
      if(myAnswer){
        // Generate user answer from myAnswer
        const userAnswer = myAnswer.map((answer, index) => {
          const number = index + 1;
          const letter = letterMapping[answer.value];
          const pair = `${number}-${letter}`;
          return {
            pair,
            isCorrect: pair === actualAnswer[index]
          };
        });
        if(returnWithColor == true){
            return   userAnswer.map(answer => ({
                    text: answer.pair,
                     color: answer.isCorrect ? 'green' : 'red'
            }))
        }else{
            return actualAnswer.join(', ')
        }
      }
      return actualAnswer.join(', ')
      
    },
    getAlphabet(index) {
    // Convert number to corresponding lowercase letter (0 = a, 1 = b, etc.)
      return String.fromCharCode(97 + index);
    },
    downloadReportFromUrl (url) {
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("target", "_blank");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
  openTestReport(){
    if(this.testReportURL){
      window.open(this.testReportURL, '_blank');
      return;
    }
  },
  onGenerationCompleted(testReportURL){
    this.testReportURL = testReportURL;
    this.downloadLoader = false;
    this.shouldMountReportsGenerator = false;
  },
  onGenerationFailed(){
    this.downloadLoader = false;
  },
  openAdvancedReport(){
      window.open(this.advancedReportURL, '_blank');
  },
  async getInterviewDetails(){
      try {
          const response = await CampaignController.getInterviewReport(this.userId, this.campaignId);
          if (response.data?.result) {
              this.interviewDetails = response.data?.result?.interviewDetails;
          } else {
              console.log('response data error...', response.data.error)
          }
      } catch (err) {
          console.log(err);
      }
  },
  withinScoreRange(min, max){
      return score => { return (min <= score && score <= max)}
  },
  strandScoreSum(strand){
      let strandScores = strand.map(question => 
          question.myAnswer != undefined?
            (question.questionOptions.filter(opt=>opt.optionKey === question?.myAnswer[0]).pop()?.optionScore) : 0
      );
      return strandScores.reduce((accumulator, currentValue) => accumulator + Number(currentValue), 0);
  },
  camelCase(str) {
      str = str.toLowerCase().replace(/-/g, ' ');
      return str.replace(/(?:^\w|[A-Z]|\b\w)/g, function(word, index) {
          return index === 0 ? word.toLowerCase() : word.toUpperCase();
      }).replace(/\s+/g, '');
  },
  
  selectRating(rating, ratingValue) {
    if(rating !== ratingValue) return "interviewRate";
      switch (rating) {
          case 1: return "interviewRate1 white--text";
          case 2: return "interviewRate4"
          case 3: return "interviewRate6"
          case 4: return "interviewRate9"
          default: return "interviewRate";
      }
  },
  selectBackgroundColor(score, setBorderStyle = false){
      let backgroundColorMap = new Map([
          [1, setBorderStyle? "primary-box-red" : "secondary-box-red"],
          [2, setBorderStyle? "primary-box-orange": "secondary-box-orange"],
          [3, setBorderStyle? "primary-box-yellow": "secondary-box-yellow"],
          [4, setBorderStyle? "primary-box-green": "secondary-box-green"]
      ])
      return backgroundColorMap.get(score);
  },
    toggleProctoringData(newValue) {
      let testType = "SCREENING";

      if (newValue == 1) {
        testType = "MAINS";
      } else {
        testType = "SCREENING";
      }
      this.getProcDetailsForTeacher(testType)
    },
    async getCampaignInfo(){
      let query = "?id="+this.campaignId;
      const response = await CampaignController.getCampaign(
          query
        );
      console.log('campaign response',response)
      if(response.status == 200){
        this.campaignStages = response.data.data;
        this.assessmentType = this.campaignStages.assessmentType;
        this.campaignMetaData = this.campaignStages.campaignMetaData;
        this.reportType = this.campaignMetaData?.reportGeneration?.reportType;
        if(this.campaignStages?.stages?.length > 0){
          this.isVgosTeacher = true;
        }
      }
    },
    getPsychometricEvaluation(score, maxScore) {
      const percentage = (score / maxScore) * 100;
      let interpretation = {};
      if (percentage <= 25) {
        interpretation = {
          message:
            "Below Average - The participant has below average abilities in psychometric and socio-emotional dimension. Thourough validation is recommended during subsequent stage of the recruitment process before decision of hiring is taken.",
          color: "#F4CCCC",
        };
      } else if (percentage > 25 && percentage <= 50) {
        interpretation = {
          message:
            "Average - The participant has average abilities in psychometric and socio-emotional dimension. Further validation is recommended during subsequent stages of the recruitment process.",
          color: "#FFF2CC",
        };
      } else if (percentage > 50 && percentage <= 75) {
        interpretation = {
          message:
            "Appropriate - The participant has appropriate abilities in psychometric and socio-emotional dimension. He/she is recommended for the position and may required some scaffolding as per the need of the school",
          color: "#D0E0E3",
        };
      } else {
        interpretation = {
          message:
            "Excellent - The participant has excellent abilities in psychometric and socio-emotional dimension. He/she is best suited for the position and will be the asset to the school",
          color: "#D8EAD3",
        };
      }
      return interpretation;
    },
   async getProcDetailsForTeacher(testType){
      const testRef = doc(
        db,
        "proctoredtests",
        this.campaignId,
        this.userId,
        testType
      );
      const test = await getDoc(testRef);
      
      // Reset proctoring data
        this.proctoringData = {
          "Copy/Paste": 0,
          "Exit Fullscreen": 0,
          Screenshot: 0,
          "Switch Tab/Browser": 0,
        };
        if (this.userProctoringActivities?.length > 0) {
          this.userProctoringActivities.splice(0);
        }

        if (this.cumulativeProctoringData?.length > 1) {
          this.cumulativeProctoringData.splice(1);
        }

      if (test.exists() && test.data().proctoringType !== "NONE") {
        const activities = test.data().activities || [];
        
        for (const temp of activities) {
          if (temp.activity in this.proctoringData) {
            this.proctoringData[temp.activity] += 1;
          }
          this.userProctoringActivities.push(temp);

          if (temp.activity !== "Started" && temp.activity !== "Completed") {
            let flag = true;
            for (let i = 0; i < this.cumulativeProctoringData.length; i++) {
              // if activitiy already exists
              if (this.cumulativeProctoringData[i][0] === temp.activity) {
                this.cumulativeProctoringData[i][1] += 1;
                flag = false;
                break;
              }
            }
            // if a new activity is found
            if (flag) {
              this.cumulativeProctoringData.push([
                temp.activity,
                1,
                this.generateRandomColor(),
              ]);
            }
          }
        }
        this.addDataToChart();
      } else {
        console.log("No proctoring data available or proctoring type is NONE");
      }
    },
    isCorrectResponse(myAnswer, correctAns, markingType = 'regular marking') {
      if(markingType && markingType.toLowerCase() == 'likert marking')
      {
        return true;
      }
      if (myAnswer && correctAns) {
        let sortedStr = correctAns?.split(',')
          ?.map(ans => ans.trim())
          ?.filter(answer => /^[A-Ea-e]$/
          ?.test(answer))
          ?.sort()?.join("") || '';
        myAnswer.sort();
        return myAnswer.join("") === sortedStr ? true : false;
      } else {
        return false;
      }
    },
    getParams() {
      this.campaignId = this.$route.params.campaignId || this.$route.query.campaignId;
      this.userId = this.$route.params.userId;
      this.studentId = this.$route.query.studentId;
      this.grade = this.$route.query.grade;
      this.campaignName = this.$route.query.campaignName;
      this.campaignType =  this.$route.query.campaignType;
      this.assessmentId = this.$route.query.assessmentId;
    },

    logout() {
      AuthService.logout();
      this.$router.push("/login");
    },
    async getReportData() {
            try {
                
                const response = await CampaignController.getReportData(this.userId, this.campaignId);
                if (response.data?.result) {
                    this.userReportData = response.data.result;
                    this.numberOfRounds = this.userReportData.numberOfRounds;
                    this.currentTestRoundIndex = this.userReportData.currentTestRoundIndex;
                    this.isSelect = [0,1].includes(Number(this.$route.query.isSelect))?  Number(this.$route.query.isSelect) : this.numberOfRounds[0] == 'mains' ? 1 : 0;
                    this.selectedUserName = this.userReportData?.name;
                    this.selectedResultForReport.userName = this.selectedUserName;
                    this.campaignName = this.userReportData?.campaignName;
                    this.userType = this.userReportData.userType.toLowerCase();
                    this.hasMains = this.userType == 'teacher'? this.resultData.length>0 : this.resultData.length>1;
                    this.subjects = this.userReportData.coreSkillsSubject.map(subject=>subject.subjectName)
                    this.leagueData = this.userReportData.coreSkillsSubject;
                    this.advancedReportURL = this.userReportData?.advancedReportURL;

                    this.disableAdvanceReportDownload = !this.advancedReportURL;
                    
                    if(response.data?.result?.proctoringPic){
                        this.proctoringPic = response.data?.result?.proctoringPic
                    } 
                    
                    this.userName = this.userReportData.name;
                    this.userEmail = this.userReportData.email;
                    this.userPhone = this.userReportData.phone;
                    this.schoolName = Array.isArray(this.userReportData.schoolName) ? this.userReportData.schoolName.join(',') : this.userReportData.schoolName;
                    this.displaySchoolName = Array.isArray(this.userReportData.schoolName) ? this.userReportData.schoolName.join(',').substr(0, 100) + '..' : this.userReportData.schoolName;
                    if(typeof this.userReportData.levels == 'string'){
                        this.levels.push(this.userReportData.levels)
                    } else {
                        this.levels = this.userReportData.levels
                    }
                      
                    this.mainsUserScore = this.userReportData.mainsTest.netScore;
                    
                    this.mainsTestData = this.userReportData.mainsTest                    

                } else {
                    console.log('response data error...', response.data.error)
                    alert("Something went wrong");
                }
            } catch (err) {
                console.log(err);
            } finally {
                this.preloader = false;
            }
        },
    async getResult() {
      try{
        this.targetUserId = this.userId;
        const response = await CampaignController.getResult(this.userId,this.campaignId);
        if (response.status == 200) {
          if (response.data.data.length == 0) {
            alert("User test not Started");
            // this.btnLoading = false;
          } else {
            if(this.numberOfRounds[0] == 'mains'){
              response.data.data.splice(0, 0, {})
            }
            this.resultData = response.data.data;
            this.firstTestName = this.resultData[0].testType;
            if(!this.isVgosTeacher){
        
            if(this.numberOfRounds.length > 0 ){
              this.numberOfRounds.forEach((item, index)=>{
                if(index < this.currentTestRoundIndex){
                   this.stages.js.find((stage)=> stage.status == item.toUpperCase()).disable = false
                }
                
              })
            }else {
              this.resultData.forEach((item, index)=>{
                this.stages.js[index].disable = false
              })
            }
            
            this.$store.dispatch('setStages', this.stages.js) 
            }else{
              this.$store.dispatch('setStages', this.stages.teacher) 
            }
             
            this.questionDialog = true;
            this.switchResult(this.isSelect);
            this.selectedUser = this.userId;
            // this.selectedUserName = `${this.userData.personalInfo.firstName} ${(this.userData.personalInfo.middleName || '')} ${this.userData.personalInfo.lastName}`;
            this.bubbleChartOptions.title = this.selectedUserName;

            if( this.hasInterview && !this.isVgosTeacher){
            await this.getInterviewDetails();
           }
            if( this.resultData.length > 1 || (this.resultData.length == 1 && this.isVgosTeacher == true)) {
                let mainsData  = this.resultData.filter(item=>item.testType=='MAINS').pop();
            
                let psychometryData = mainsData.skills.filter(item=>item.skillName=='Psychometry').pop();
                let questionsData = psychometryData.questions;
                            
                let strandsData = Object.groupBy( questionsData , ({strand})=>strand)
                let strandsScore = {};
                for (const strand in strandsData){
                    strandsScore[strand] = this.strandScoreSum(strandsData[strand]);
                }
                for (const strand of Object.keys(strandsScore)){
                    if(this.psychometryScoreMap.has(strand)){
                        const score = strandsScore[strand];
                        for (const [checkScoreRange,newScore] of this.psychometryScoreMap.get(strand)){
                            if (checkScoreRange(score)){
                                this.$set(this.normalizedStrandsScore, this.camelCase(strand), newScore);
                                break;
                            }
                        }
                    }
                }
                this.disablePsychometryDownload = false;
                }
  
          }
        } else {
          alert(response.data.error);
        }
      } catch(err){
        console.log(err);
      }
    },

    goBack() {
      if (window.opener) {
        window.close();
      } else {
        this.$router.back();
      }
    },
    goToCampaigns() {
      if (window.opener) {
        window.opener.location.href = '/#/campaign';
        window.close();
      } else {
        this.$router.push('/#/campaign');
      }
    },
    generateRandomColor() {
      const minRed = 255;
      const maxRed = 255;
      const minGreen = 255;
      const maxGreen = 0;
      const minBlue = 0;
      const maxBlue = 0;

      const randomRed = Math.floor(
        Math.random() * (maxRed - minRed + 1) + minRed
      );
      const randomGreen = Math.floor(
        Math.random() * (maxGreen - minGreen + 1) + minGreen
      );
      const randomBlue = Math.floor(
        Math.random() * (maxBlue - minBlue + 1) + minBlue
      );

      const randomColor = `rgb(${randomRed},${randomGreen},${randomBlue})`;

      return randomColor;
    },

    demoReport() {
      // console.log("data demo videos...???", this.dataDemoVideos);
      // this.dataDemoVideos.sort((x, y) => x.date.localeCompare(y.date));

      // this.dataDemoVideos = this.dataDemoVideos[this.dataDemoVideos.length - 1]
      // localStorage.setItem("videoInfo", JSON.stringify(this.dataDemoVideos))
      // this.$store.commit("setVideoDetails", this.dataDemoVideos)

      this.$router.push({ path: `/demoreport/${this.campaignId}/${this.userId}`, query: { ...(this.$route.query.redirectedFrom && {redirectedFrom: this.$route.query.redirectedFrom}) } });
      // this.demoDialog = true
    },


    showProc() {
      if (this.bubbleChartData.length > 1) {
        this.proctorDialog = true;
      } else {
        alert("Proctoring data not found");
      }
    },
    sortSkillsByCore(skillsArray) {
    
    const sortedArray = [...skillsArray];
    
    // Sort the array
    sortedArray.sort((a, b) => {
        if (a.isCoreSkill === b.isCoreSkill) {
            return 0;
        }
        // If a is core skill and b is not, a should come first
        if (a.isCoreSkill) {
            return -1;
        }
        // If b is core skill and a is not, b should come first
        return 1;
      });
    
      return sortedArray;
    },
    evaluateFillInTheBlanksAnswers(selectedAnswer, correctAnswers, isCaseSensitive) {
      if(isCaseSensitive){
        return this.normalizedSpaceEqual(selectedAnswer, correctAnswers);
      }
      return this.normalizedSpaceEqual(selectedAnswer.toLowerCase(), correctAnswers.toLowerCase());
    },
    normalizedSpaceEqual(correctAnswer, myAnswer) {
      const normalize = (str) => str.replace(/\s+/g, ' ').trim();
      return normalize(correctAnswer) === normalize(myAnswer);
    },
    async switchResult(index) {
      this.isSelect = index
      if(index == undefined){
        return;
      }
      this.$router.replace({query:{isSelect:index, ...(this.$route.query.redirectedFrom && {redirectedFrom: this.$route.query.redirectedFrom})}})
      if ([0,1].includes(index)) {
        this.isSelect = index;
        !this.isVgosTeacher ? await this.toggleProctoringData(index) : await this.getProcDetailsForTeacher("MAINS");
        this.resultData[index].skills = this.sortSkillsByCore(this.resultData[index].skills);
        this.selectedResult = this.resultData[index];
        
        this.totalScore= this.campaignMetaData?.isSkillLevelScoreNormalized ? 
          this.selectedResult?.netScore
          : this.selectedResult?.skills?.reduce((acc, {score})=> acc + (score || 0), 0);
        this.maxTime = this.selectedResult.maximumTime / 60;
        this.maxMarks = this.campaignMetaData?.isSkillLevelScoreNormalized ? 10 : this.selectedResult?.maxMarks;
        this.userTime = (this.selectedResult.timeTakenByUser / 60).toFixed(2);
        this.selectedResult.testTakenOn = this.reformatDate(this.selectedResult.testTakenOn);

        if (this.selectedResult == null) {
          alert("Test not started yet");
          this.switchResult(0);
        } else {
           // Selecting a Core Skill where questions exist
            this.selectedSkill = this.resultData[index]?.skills.find(({isCoreSkill, questions}) => isCoreSkill && questions?.length > 0) || this.resultData[index]?.skills[0];
            this.selectedSkill.totalTimeTaken = this.getTotalTimeForSkill(this.selectedSkill.questions);
            this.selectedSkillName = this.selectedSkill.skillName
            this.currenrSkillIndex = index;
            this.progressLevel =
              (this.selectedSkill.score / this.selectedSkill.maximumMarks) * 100;
            this.selectedResult.skills.forEach(({questions})=>questions?.forEach(question=>this.setAnswerCorrectness(question)))
            this.selectedResultForReport = cloneDeep(this.selectedResult.skills.filter( 
              skill => skill.questions && skill.questions.length > 0
            ));
            this.selectedResultForReport.overallNetScore = this.selectedResult?.netScore;
            this.selectedResultForReport.userName = this.selectedUserName;
            this.downloadLoader = true;
            this.setTestReportURL();
            if(!this.testReportURL)this.shouldMountReportsGenerator = true;
            this.scrollToFirstQuestion();

            let skillPromises = this.selectedResult.skills?.map(async (skill) => {
              if (!skill.questions || !Array.isArray(skill.questions) || skill.questions.length === 0) {
                return; // Skip this skill if it has no questions
              }

              const questionPromises = skill.questions.map((question) => this.resolveQuestionMedia(question));

              await Promise.all(questionPromises || []);
            });

            Promise.all(skillPromises?.filter(Boolean) || []).then(async()=> {
              await this.$nextTick();
            });
          }
      } else if (index === 2) {
        this.demoReport();
      }else if (index === 3) {
        this.$router.push({ path: `/interviewReport/${this.campaignId}/${this.userId}`, query: { ...(this.$route.query.redirectedFrom && {redirectedFrom: this.$route.query.redirectedFrom}) } });
      }
    },
    setTestReportURL(){
      if (this.isSelect === 0 || this.isSelect === 1) {
        this.testReportURL = this.isSelect === 0
          ? this.selectedResult?.testReportURL_screening
          : this.selectedResult?.testReportURL;
        if(this.testReportURL){
          this.downloadLoader = false;
        }
      }
    },
    reformatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },
    addDataToChart() {
      this.bubbleChartData.splice(1);
      
      if (this.userProctoringActivities.length === 0) {
        console.log("No proctoring activities to add to chart");
        return;
      }

      this.bubbleChartOptions.hAxis.viewWindow.max =
        parseInt(
          this.timeToMinutes(
            this.userProctoringActivities[
              this.userProctoringActivities.length - 1
            ].time
          ) - this.timeToMinutes(this.userProctoringActivities[0].time)
        ) + 1;

      this.bubbleChartOptions.vAxis.viewWindow.max = 1;

      for (const act of this.userProctoringActivities) {
        // this.bubbleChartData.push([act.activity, this.timeToMinutes(act.time) - this.timeToMinutes(this.userProctoringActivities[0].time), act.time, act.activity, act.ques])
        this.bubbleChartData.push([
          act.activity,
          this.timeToMinutes(act.time) -
            this.timeToMinutes(this.userProctoringActivities[0].time),
          act.ques,
          act.activity,
          this.timeToMinutes(act.time) -
            this.timeToMinutes(this.userProctoringActivities[0].time),
        ]);

        this.bubbleChartOptions.vAxis.viewWindow.max = Math.max(
          this.bubbleChartOptions.vAxis.viewWindow.max,
          act.ques + 1
        );
      }
    },

    timeToMinutes(timeString) {
      var timeParts = timeString.split(":");
      var hours = parseInt(timeParts[0]);
      var minutes = parseInt(timeParts[1]);
      var seconds = parseInt(timeParts[2]);
      return (hours * 3600 + minutes * 60 + seconds) / 60;
    },

    getSelectedSkillData(skill, index) {
      if(this.assessmentType.toLowerCase() !== 'dynamic') return;
      console.log("skill :", skill);
      console.log("selectedResult :", this.selectedResult);
      this.currenrSkillIndex = index;
      this.selectedSkill = skill;
      this.scrollToFirstQuestion()
      // this.selectedSkillName = skill.skillName
      console.log("showing skills", this.selectedSkill);
      this.selectedSkill.totalTimeTaken = this.selectedSkill?.totalTimeTaken || this.getTotalTimeForSkill(this.selectedSkill.questions);

      this.selectedSkill.questions.forEach((obj) => this.setAnswerCorrectness(obj));
      this.progressLevel =
        (this.selectedSkill.score / this.selectedSkill.maximumMarks) * 100;
    },
    scrollToFirstQuestion(){
      let element = document.querySelectorAll('.skill-section-top')[0];
      if(element){
        element.scrollIntoView({
          block: 'nearest',
          inline: 'nearest',
          behavior: 'instant'
        });
      }
    },

    getTotalTimeForSkill(questionsForSelectedSkill){
      const totalTime = questionsForSelectedSkill.reduce((totalTimeTaken, {timeTaken})=>{
        return Number(timeTaken) > 0 ? totalTimeTaken + Number(timeTaken) : totalTimeTaken;
      }, 0)

      return (totalTime / 60).toFixed(2) || 0;
    },

    getDate(timeStamp) {
      return this.formatDate(timeStamp);
    },
    // calculateUsersForLeague(users) {
    //   users.forEach((user) => {
    //     var coreSkills = [];
    //     if (user.coreSkillsSubject !== undefined) {
    //       user.coreSkillsSubject.forEach((coreSkillSubject) => {
    //         if (coreSkillSubject.result == "PASSED") {
    //           coreSkills.push(coreSkillSubject);
    //         }
    //       });
    //     }
    //     user.coreSkillsSubject = coreSkills;
    //   });
    //   return users;
    // },
    async fetchCampaignAudience(id) {
      const campaignRef = collection(db, "campaigns", id, "audience");
      const campDetailsRef = doc(db, "campaigns", id);

      const docSnap = await getDoc(campDetailsRef);

      if (docSnap.exists()) {
        // console.log("camp details here..?", docSnap.data())
        const campDetails = docSnap.data();
        this.campName = campDetails.name;
        // console.log("name of the campaign..?", this.campName)
      }

      // console.log("heyyylooooo");
      let unsubscribe = onSnapshot(campaignRef, (snapshot) => {
        this.dataTableLoading = false;
        this.users = [];
        this.preSearchUser = [];
        var preUsers = [];
        if (snapshot.empty) {
        } else {
          this.count = snapshot.docs.length;
          snapshot.docs.forEach(async (doc) => {
            // this.users.push(doc.data());
            this.preSearchUser.push(doc.data());
            preUsers.push(doc.data());
          });
          this.users = this.calculateUsersForLeague(preUsers);

          // this.preSearchUser = response.data.audience;
          // var preUsers = response.data.audience == null ? [] : response.data.audience;
          this.chartData.invited = this.filterByStatus(
            preUsers,
            "INVITED"
          ).length;
          this.invited = this.filterByStatus(preUsers, "INVITED").length;
          this.screeningStarted = this.filterByStatus(
            preUsers,
            "SCREENING STARTED"
          ).length;
          this.screeningPassed = this.filterByStatus(
            preUsers,
            "SCREENING PASSED"
          ).length;
          this.mainsStarted = this.filterByStatus(
            preUsers,
            "MAINS STARTED"
          ).length;
          this.mainsPassed = this.filterByStatus(
            preUsers,
            "MAINS PASSED"
          ).length;
          this.screeningFailed = this.filterByStatus(
            preUsers,
            "SCREENING FAILED"
          ).length;
          this.mainsFailed = this.filterByStatus(
            preUsers,
            "MAINS FAILED"
          ).length;

          this.chartData.screeningStarted = this.filterByStatus(
            preUsers,
            "SCREENING STARTED"
          ).length;
          this.chartData.screeningPassed = this.filterByStatus(
            preUsers,
            "SCREENING PASSED"
          ).length;
          this.chartData.mainsStarted = this.filterByStatus(
            preUsers,
            "MAINS STARTED"
          ).length;
          this.chartData.mainsPassed = this.filterByStatus(
            preUsers,
            "MAINS PASSED"
          ).length;
          this.chartData.screeningFailed = this.filterByStatus(
            preUsers,
            "SCREENING FAILED"
          ).length;
          this.chartData.mainsFailed = this.filterByStatus(
            preUsers,
            "MAINS FAILED"
          ).length;
          this.apraisals = this.filterByStatus(preUsers, "MAINS PASSED").length;
          this.pip = this.filterByStatus(preUsers, "MAINS FAILED").length;
          this.tbd = this.filterByStatus(preUsers, "SCREENING FAILED").length;
        }
        // console.log("this.chartData", this.chartData)
      });
      this.liveListenersToUnsubscribeMixin.push(unsubscribe);
    },
    async fetchUserReport(studentId, campaignId, grade, assessmentId) {
      const response = await StaticAssessmentController.getUserTestReport(studentId, campaignId, grade, assessmentId);
      if(response.status == 200) {
        const data =  response.data.data;
        this.targetUserId = data.assessmentResultId;
          this.selectedSkill.questions = data.questionResponse??[];
          this.selectedSkill.questions = this.selectedSkill.questions.map((question)=>{
            this.setAnswerCorrectness(question);
            question.level = data.level;
            question.grade = Array.isArray(this.grade)? this.grade[0] : this.grade;
            const {questionId, ...rest} = question;
            return {
                questionNumber: questionId.split("_Q")[1],
                ...rest
              }
          })
          this.prefferedLanguage = Array.isArray(data.prefferedLanguage)? data.prefferedLanguage[0]: data.prefferedLanguage ?? '';
          this.selectedSkill.totalTimeTaken = this.getTotalTimeForSkill(this.selectedSkill.questions);
          if(data?.testReportURL){
            this.testReportURL = data.testReportURL;
          }
          this.selectedSubjectName = data.subjectsScore[0].subject;
          
          const isCoreSkill = data.subjectsScore?.[0]?.isCoreSkill;

          if (isCoreSkill === undefined || isCoreSkill === true) {
              this.questionsToDisplay = this.selectedSkill.questions.filter(question =>
              question.subject.includes(this.selectedSubjectName)
              );
          } else {
              this.questionsToDisplay = this.selectedSkill.questions.filter(question =>
              question.english.strand.includes(this.selectedSubjectName)
            );
          }
          this.selectedUserName = `${data.firstName} ${data.lastName}`
          this.userEmail = data.email;                    
          this.selectedSkill.score = data.overallScore.totalSecuredScore;
          this.selectedSkill.maximumMarks = data.overallScore?.totalScore;
          this.progressLevel = ((this.selectedSkill.score / this.selectedSkill.maximumMarks)*100);
          this.selectedSkill.subjectsScore = data.subjectsScore;
          this.selectedSkill.skillName = data.skills[0];
          this.selectedResultForReport = [cloneDeep(this.selectedSkill)];
          this.selectedResultForReport.overallNetScore = data.netScore;
          this.selectedResultForReport.userName = this.selectedUserName;
          this.selectedSkillName = data.skills[0];
          this.selectedResult.skills = data.skills.map(skill=>({
            skillName: skill,
            isCoreSkill: skill==='Core Skills',
            score: data.overallScore?.totalSecuredScore,
            maximumMarks: data.overallScore.totalScore
          }));
          this.totalScore = data.netScore;
          this.userTime = data.timeTakenByUser??'';
          this.maxTime = data.totalDuration;
          this.selectedResult.testTakenOn = this.reformatDate(data.testTakenOn);
          this.advancedReportURL = data?.advancedReportURL;
          this.disableAdvanceReportDownload = !this.advancedReportURL;
          this.preloader = false;
          if(!this.testReportURL){
            this.downloadLoader = true;
            this.shouldMountReportsGenerator = true;
          }

          let questionPromises = this.selectedSkill.questions.map(question => this.resolveQuestionMedia(question))

          await Promise.all(questionPromises)
      }
    },
    resolveMediaType(assetType) {
      const map = { audio: 'AUDIO', video: 'VIDEO', image: 'IMG' };
      return map[assetType] || 'IMG';
    },
    // Resolves the media of a question and, for a passage based question, of every
    // sub-question rendered under it.
    async resolveQuestionMedia(question) {
      if (!question) return;
      if (question.questionAssetUrl && question.questionAssetUrl !== "NA") {
        await this.resolveQuestionAssetUrl(question);
      }
      const optionsPromises = (Array.isArray(question.questionOptions) ? question.questionOptions : []).map(async (option) => {
        if (
          option.optionValue?.startsWith("https://") ||
          option.optionValue?.startsWith("http://") ||
          option.optionValue?.startsWith("blob:")
        ) {
          await this.resolveQuestionOptionUrl(option);
        }
      });
      await Promise.all(optionsPromises);
      await Promise.all((question.subQuestions || []).map((subQuestion) => this.resolveQuestionMedia(subQuestion)));
    },
    async resolveQuestionAssetUrl(question) {
      let url = question.questionAssetUrl;
      if(url?.startsWith('https://drive.google.com/')) {
        let fileId = null;
        if (url.indexOf('&id=') > -1) {
          fileId = url.split('&id=').pop();
        } else {
          // Regular expression pattern to extract file ID
          const pattern = /\/file\/d\/([^\\/]+)\//;
          fileId = url.match(pattern)[1];
        }
        try {
          const response = await AssessmentController.doFetchFileContentFromDriveUrl(fileId);
          if (response.status === 200) {
            question.questionAssetUrl = window.URL.createObjectURL(new Blob([response.data]));
            question.mediaType = this.resolveMediaType(question.questionAssetType);
          } else {
            question.mediaType = 'ERR';
          }
        } catch (error) {
          console.error('Error fetching file content:', error);
          question.mediaType = 'ERR';
        }
      }
      else{
        question.mediaType = this.resolveMediaType(question.questionAssetType);
      }
    },
    async resolveQuestionOptionUrl(option) {
      let url = option.optionValue;
      if(url?.startsWith('https://drive.google.com/')) {
        let fileId = null
        if(url.indexOf('&id=') > -1) {
          fileId = url.split('&id=').pop()
        } else {
          // Regular expression pattern to extract file ID
          const pattern = /\/file\/d\/([^\\/]+)\//;
          fileId = url.match(pattern)[1];
        }
        try{
          const response = await AssessmentController.doFetchFileContentFromDriveUrl(fileId);
          if(response.status === 200) {
            option.optionValue = window.URL.createObjectURL(new Blob([response.data]));
            option.mediaType = 'IMG'
          }
          else {
            option.mediaType = 'ERR';
          }
        }
        catch(error){
          console.error('Error fetching file content:', error);
          option.mediaType = 'ERR';
        }
      }
      else{
        option.mediaType = 'IMG'
      }
    },
    imageInOption(questionOptions){
      // MATCH_THE_FOLLOWING stores questionOptions as a "key:value, key:value" pair
      // string (see prepareOptions) rather than an option array, so the type has to
      // be checked — `?.` only guards null/undefined and .some still blew up.
      if (!Array.isArray(questionOptions)) {
        return false;
      }
      return questionOptions.some(option=>(option.optionValue?.startsWith('https://') || option.optionValue?.startsWith('http://') || option.optionValue?.startsWith('blob:')));
    },
    returnCorrectAnswerAssetURL(question){
      let correctOption = question.questionOptions.find(({optionKey})=>question.myAnswer.includes(optionKey))
      return correctOption?.mediaType ? correctOption.optionValue : this.loadingAssetURL;
    },
    imgError(item){
      item.mediaType = 'OBJ'
    },
    objError(item){
      item.mediaType = 'ERR'; 
    },

    async downloadPDF() {
    if(this.testReportURL){
      window.open(this.testReportURL, '_blank');
      return;
    }
    console.time('test report download')
    this.downloadLoader = true;
    const allQuestions = Array.from(document.querySelectorAll('div[aria-label^="pdf-page-"]'));

    const convertImgToBase64 = async () => {
    const imageElements = document.querySelectorAll('.pdf-image');
    Array.from(imageElements).forEach(img => {
      if (img.src.includes('firebasestorage.googleapis.com')) {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, img.width, img.height);
          img.src = canvas.toDataURL('image/png');
        } catch (error) {
          console.error('Error converting image to base64:', error);
        }
      }
    });
  }; 
    
    // Function to process MathML elements with MathJax
    const processMathMLElements = async (element) => {
        const mathElements = element.querySelectorAll('math'); // Find all MathML elements
        for (const mathElement of mathElements) {
            const mathContent = mathElement.innerHTML.trim();
            if (mathContent) {
                // Use a div to allow MathJax to render properly
                mathElement.outerHTML = `<div style="font-size: 16px; display: inline-block;">${mathContent}</div>`;
            } else {
                mathElement.outerHTML = '<div style="font-size: 16px; color: red;">[Empty MathML]</div>';
            }
        }
        // Typeset the math elements using MathJax
        await MathJax.typesetPromise([element]);
    };

    const createChunkContainer = (questions) => {
      const container = document.createElement('div');
      container.style.cssText = `
        width: 100%;
      `;
      container.classList.add('pdf-render-only'); 
      
      questions.forEach(question => {
        const questionClone = question.cloneNode(true);
        container.appendChild(questionClone);
      });
      
      return container;
    };

    // Process each page to handle MathML
    for (const question of allQuestions) {
        await processMathMLElements(question);
    }
    // convert all firestorage url images to base64 before pdf render
    await convertImgToBase64();
    // Wait for all MathJax typesetting to finish
    await MathJax.typesetPromise();
    
    // Chunk the questions into groups
    const chunkSize = 100;
    const chunks = [];
    
    if (allQuestions.length > 2) {
      chunks.push(allQuestions.slice(0, 2));
    }

    for (let i = 2; i < allQuestions.length; i += chunkSize) {
      chunks.push(allQuestions.slice(i, i + chunkSize));
    }
  
    // PDF options
    const pdfOptions = {
        margin: 1,
        filename: 'document.pdf',
        image: { type: 'jpeg', quality: 1 },
        html2canvas: { scale: 1 },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' },
    };

    // Initialize worker for pdf generation
    let worker = html2pdf().set(pdfOptions);


    if (chunks.length > 0) {
        // process each chunk seperately to prevent browser canvas contraints breach
        for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
          console.log('Chunk no', chunkIndex)
          const chunk = chunks[chunkIndex];
          if (chunk.length === 0) continue;
          const chunkContainer = createChunkContainer(chunk);
          
          window.scrollTo(0, 0); // Reset scroll position
          await this.$nextTick();
          if (chunkIndex === 0) {
            // First chunk: create the base PDF
            await worker.from(chunkContainer)
              .toContainer()
              .toCanvas()
              .toPdf();
          } else {
            // Later chunks: get the pdf and append pages
            await worker.get('pdf')
              .then((pdf) => {
                pdf.addPage();
              });

            await worker.from(chunkContainer)
              .toContainer()
              .toCanvas()
              .toPdf();
          }
        }
    }

    // Save the PDF once all pages are processed
    await worker.save();
    this.downloadLoader = false;
    console.timeEnd('test report download')
},
    async generatePsychometryReport(){
      this.downloadLoader = true;

      // generate filename
      const fileName = this.generatePsychometryReportFileName()
      // pdf config
      const options = {
        margin: 0,
        filename: fileName,
        image: {
          type: 'jpeg',
          quality: 0.98
        },
        html2canvas: {
        scale: 2,
        useCORS: true,
        logging: false,
        letterRendering: true,
        // windowHeight: 11.7 * 96,
        // windowWidth: 16.5 * 96,
        },
        jsPDF: {
          unit: 'in',
          format: 'a3',
          orientation: 'landscape',
          compress: true
        },
        pagebreak: { 
          mode: ['avoid-all', 'css', 'legacy']
        }
      };
      // Generate PDF
      const report = await html2pdf()
        .set(options)
        .from(this.$refs.psychometryReportTemplate.innerHTML)
        .save();
      
      this.downloadLoader = false;
    },
    generatePsychometryReportFileName(){
      let userName = this.userName.split(" ");
      console.log('userName',userName)
      let firstName = userName[0];
      let lastName = userName[1];
      return `${firstName}_${lastName}_${this.levels[0]}_psychometry_report`
    }
  },
  getPercentage(value, total){
    return (value/total)*100;
  },
};
</script>

<style scoped>
/* The passage card is sized by its text alone - it never scrolls and never clips. */
.passage-statement-card {
  height: auto;
  max-height: none;
  overflow: visible;
}
.passage-statement.passage-statement--plain-text {
  /* Plain text passages carry their paragraph breaks as newlines */
  white-space: pre-line;
}
.mobile-test-report-shell {
  /* position:fixed takes this fully out of the page's normal document flow, so it's
     immune to whatever ancestor (v-main/app wrap/etc.) was still allowing the whole
     block to scroll as one piece — the header stays put no matter what. */
  position: fixed;
  inset: 0;
  height: 100vh;
  height: 100dvh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 1;
}
.mobile-test-report-sticky-header {
  flex: 0 0 auto;
  background: var(--white);
}
.mobile-test-report-header {
  height: 64px;
}
.mobile-test-report-tabs {
  margin: 0 4px;
}
.mobile-test-report-tabs ::v-deep .v-tab--active,
.mobile-test-report-tabs ::v-deep .v-tab--active.v-tab--disabled {
  color: var(--blue-01) !important;
  box-shadow: inset 0 -2px 0 0 var(--blue-01);
}
.mobile-candidate-card {
  border: 1px solid var(--gray-03);
}
.mobile-detail-row {
  padding: 6px 0;
  font-size: 14px;
}
.mobile-report-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  background: var(--white);
  padding-bottom: 16px;
}
.mobile-total-score-card {
  border: 1px solid var(--gray-03);
}
.mobile-score-download-btn {
  border-color: #3F41D1 !important;
}
.mobile-score-bar-wrapper {
  position: relative;
  padding: 18px 0;
}
.mobile-score-marker {
  position: absolute;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  color: #212121;
}
.mobile-score-marker--user {
  top: 0;
}
.mobile-score-marker--passing {
  bottom: 0;
}
.mobile-skill-cards-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  padding-bottom: 16px;
}
.mobile-skill-card {
  border: 1px solid var(--gray-03);
  cursor: pointer;
}
.mobile-skill-card--disabled {
  opacity: 0.5;
  cursor: default;
}
.mobile-skill-card-row {
  font-size: 13px;
  padding: 2px 0;
}
.v-application .default-skill-card{
  color: var(--gray-04) !important;
  background-color: #ebebeb !important;
}
.v-application .default-assessment-skill-card{
  color: var(--blue-01) !important;
  background-color: #EBF2FF !important;
}
.v-application .error-skill-card{
  background: rgba(230, 57, 62, 0.1)!important;
}

.v-application .success-skill-card{
  background: rgba(6, 194, 112, 0.1) !important;
}

.page-wrapper { 
  position: relative;
}

.pdf-header img,
.pdf-footer img {
  max-width: 100%;
  height: auto;
}

.content {
  position: relative;
  z-index: 1;
}
.dots {
  flex: 1;
  margin: 0 8px;
  border-bottom: 2px dotted #212121;
}
.user-performance-report-progress-green::-webkit-progress-value {
  background-color: #06C270;
}
.user-performance-report-progress-orange::-webkit-progress-value {
  background-color: #FFB86B;
}
.user-performance-report-progress-green::-webkit-progress-bar, .user-performance-report-progress-orange::-webkit-progress-bar {
  background-color: #E0E0E0;
}
.primary-box-red{
    background-color: #D4002829 !important;
    border: 1px solid #D40028;
    color: #D40028 !important;
}
.primary-box-orange{
    background-color: #FB750029 !important;
    border: 1px solid #FB7500 !important;
    color: #FB7500 !important;
}
.primary-box-yellow{
    background-color: #FADC0029 !important;
    border: 1px solid #FADC00;
    color: #FADC00 !important;
}
.primary-box-green{
    background-color: #06C27029 !important;
    border: 1px solid #06C270 !important;
    color: #06C270 !important;

}
.secondary-box-red{
  background-color: #D4002829 !important;
}
.secondary-box-orange{
  background-color: #FB750029 !important;
}
.secondary-box-yellow{
  background-color: #FADC0029 !important;
}
.secondary-box-green{
  background-color: #06C27029 !important;
}
.core-skills-card-border {
  border: 4px solid #007C2A;
  border-radius: 8px;
  border-image: linear-gradient(261.89deg, #007C2A 0%, #029745 37.2%, #04AA58 62.29%, #05B866 81.08%, #06C270 94.9%) 1;
  border-image-slice: 1;
}
.digital-literacy-card-border {
  border: 4px solid #A72C5E;
  border-radius: 8px;
  border-image: linear-gradient(
    261.89deg,
    #A72C5E 0%,
    #BB4072 37.2%,
    #CF5486 62.29%,
    #D95E90 81.08%,
    #F77CAE 94.9%
  ); 
  border-image-slice: 1;
  border-radius: 8px;
}
.communication-skills-card-border {
  border: 4px solid #002298;
  border-radius: 8px;
  border-image: linear-gradient(
    261.89deg,
    #002298 0%,
    #0040B6 37.2%,
    #0054CA 62.29%,
    #075ED4 81.08%,
    #1B72E8 94.9%
  );
  border-image-slice: 1;
}
.pedagogy-card-border {
  border: 4px solid #622C74;
  border-radius: 8px;
  border-image: linear-gradient(
    261.89deg,
    #622C74 0%,
    #764088 37.2%,
    #8A549C 62.29%,
    #945EA6 81.08%,
    #B27CC4 94.9%
  );
  border-image-slice: 1;
}
.psychometry-card-border {
  border: 4px solid #AF5600;
  border-radius: 8px;
  border-image: linear-gradient(
    261.89deg,
    #AF5600 0%,
    #C36A0B 37.2%,
    #D77E1F 62.29%,
    #E18829 81.08%,
    #FFA647 94.9%
  );
  border-image-slice: 1;
  border-radius: 8px;
}
.core-skills-background {
  background-color: #06C27024 !important;
}
.pedagogy-background {
  background-color: #B27CC424 !important;
}
.digital-literacy-background {
  background-color: #F77CAE24 !important;
}
.communication-skills-background {
  background-color: #1B72E824 !important;
}
.psychometry-background {
  background-color: #FFA64724 !important;
}
@media print{
  .user-performance-report {
    width: 8.27in;
    height: auto;
    margin: 0;
    font-family: "GraphikRegular", sans-serif !important;
  }
  .shadow-fallback {
    border: 1px solid rgba(0, 0, 0, 0.12); /* Slight shadow effect */
    border-bottom: 2px solid rgba(0, 0, 0, 0.18); /* Enhance bottom shadow */
  }
}
.math-container {
  display: flex;
  flex-wrap: wrap;
}

.hide-it{
  position: absolute;
  top:-100000vh;

}
@media screen {
  ::v-deep(.pdf-render-only) {
    position: fixed !important;
    top: -9999px !important;
    left: -9999px !important;
    width: 1px !important;
    height: 1px !important;
    opacity: 0 !important;
    pointer-events: none !important;
    z-index: -9999 !important;
  }
}

@media print {
  .pdf-render-only {
    display: block !important;
    position: static !important;
    opacity: 1 !important;
    height: auto !important;
    width: 100% !important;
    overflow: visible !important;
  }
}
.no-scroll{
  overflow: hidden;
  width: 100%;
}
.repCard {
  margin: 8px 12px 0px 32px;
  overflow: hidden; 
  height: 80vh;
}

.title-section{
  margin: 80px 12px 0px 32px !important;
}

.username-et-email-box{
  width: 324px !important;
  justify-content: space-between;
}
.netscore{
  font-size: 20px;
  .label{
    font-weight: 300;
  }
  .score{
    font-family: 'GraphikMedium', sans-serif !important;
    font-weight: 500;
  }
}
.option-score{
  display: flex;
  align-items: center;
  border-left: 1px solid #E0E0E0;
}
.question-chart{
  width: 100%;
  height: 12.5rem;
  padding-bottom: 0.5rem;
}
.tabBtn {
  font-size: 14px;
  border-color: inherit !important;
  width: 100% !important;
  height : auto !important;
  padding: 10px !important;
  border-left-width: thin !important;
}

.viewTestProctoringBox {
  border: 1px solid rgba(0, 0, 0, 0.26);
  border-radius: 8px;
  margin-top: 8px;
  margin-bottom: 8px;
  padding: 8px;
  justify-content: space-between;
}

.pdfTitle {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin: 8px 12px;
}

.pdfSubTitle {
  margin: 8px 12px;
}

.page-break-item {
  page-break-after: always;
  /* Add a page break before each v-list-item */
}
.pdf-item{
  page-break-inside: avoid;
}

.rowSpacing {
  margin-bottom: 12px;
}

.chipSpacing {
  margin-right: 8px;
}

.pdfCard {
  padding: 4px;
}

.correctOption {
  color: green;
  padding-right: 12px;
  margin-top: 4px;
}

.otherOption {
  padding-right: 12px;
  margin-top: 4px;
}

.correctAnswer {
  color: green;
  padding-left: 4px;
}

.incorrectAnswer {
  color: red;
  padding-left: 4px;
}

.pdfHead {
  display: flex;
  flex-direction: row;
  justify-content: center;
  margin: 8px 12px;
}

.g-8 {
  gap: 8px
}
.g-16 {
  gap: 16px
}
.g-24{
  gap: 24px;
}
.w-20{
  width: 20%;
}
.w-60{
  width: 60%;
}

.core-skills-container {
  .text-subtitle-2 {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 140px;
    font-family: "GraphikMedium", sans-serif !important;
  }
}
.interviewRate, .interviewRate1, .interviewRate2, .interviewRate3, .interviewRate4, .interviewRate5, .interviewRate6, .interviewRate7, .interviewRate8, .interviewRate9, .interviewRate10 {
    margin: 0 16px;
    color: white !important;
}
.reportTableText{
    font-family: 'GraphikMedium', sans-serif !important;
    font-weight: 500 !important; 
}
.backgroundRed{
    background-color: #CD406E1A !important;
}
.backgroundOrange{
    background-color: #FEE3CC !important;
}
.backgroundYellow{
    background-color: #FEF9D6 !important;
}
.backgroundGreen{
    background-color: #EBFAF3 !important;
}
.fs-14{
    font-size: 14px; 
}
div[aria-label^="pdf-page-"] {
  margin-bottom: 20px;
  /* page-break-after: always; Ensure each div renders as a separate page in the PDF */
}
.correct-answer-image-pdf{
  border: 2px solid #06C270 !important;
  border-radius: 8px;
}
.option-image-border-pdf{
  border: 1px solid #0000001A;
  border-radius: 8px;
}
.correct-answer {
  font-size: 14px;
  color: #06C270;
  padding-left: 4px;
}

.incorrect-answer {
  font-size: 14px;
  color: #EE4D37;
  padding-left: 4px;
}
::v-deep .v-image.correct-answer-image .v-image__image {
    border: 2px solid #06C270 !important;
    border-radius: 8px;
  }
  
  ::v-deep .v-image.image-border .v-image__image {
    border: 1px solid #0000001A;
    border-radius: 8px;
  }

  .test-report-audio {
    display: block;
    width: 100%;
    max-width: 400px;
    border-radius: 8px;
  }
</style>